<template>
  <main class="financial">
    <template v-if="canAccess">
      <header v-show="!inDetail" class="financial__heading">
        <h1>Financial</h1>
      </header>
      <details v-show="!inDetail" class="financial__local">
        <summary><mdicon name="content-save-outline" :size="16" aria-hidden="true" /> Saved in this browser only</summary>
        <p>
          Local working drafts · saved only in this browser for your account. Clearing browser data deletes drafts and
          review decisions; downloaded exports cannot restore them.
        </p>
      </details>
      <p v-if="error && !inDetail" id="financial-error" class="financial__alert financial__alert--error" role="alert">{{ error }}</p>
      <p v-if="message && !inDetail" class="financial__message" role="status">{{ message }}</p>
      <p v-if="marginNotice && !inDetail" class="financial__alert" role="status">
        <mdicon name="information-outline" :size="18" aria-hidden="true" /> {{ marginNotice }}
        <button class="financial__link" type="button" :aria-disabled="marginLoading" @click="retryMargins">
          {{ marginLoading ? 'Loading margins…' : 'Retry' }}
        </button>
      </p>
      <div v-show="!inDetail" class="financial__tabs" role="tablist" aria-label="Financial sections" @keydown="onTabKey">
        <button
          v-for="item in tabs"
          :id="`financial-tab-${item.id}`"
          :key="item.id"
          class="financial__tab"
          role="tab"
          type="button"
          :aria-selected="tab === item.id"
          :aria-controls="`financial-panel-${item.id}`"
          :tabindex="tab === item.id ? 0 : -1"
          @click="showTab(item.id)"
        >
          <mdicon :name="item.icon" :size="18" aria-hidden="true" /> {{ item.label }}
          <span v-if="item.count !== null" class="financial__tab-count">{{ numberLabel(item.count) }}</span>
        </button>
      </div>
      <BookingPeriod
        v-show="!inDetail && (tab === 'shipments' || (tab === 'customers' && customerScope === 'booked'))"
        :key="owner"
        :entries="ledger"
        :busy="busy || matching || tab === 'invoices'"
        :loading="ledgerLoading"
        :opening="loading"
        :progress="ledgerProgress"
        :result="ledgerResult"
        :loaded-periods="loadedPeriods"
        @change="changeBookingPeriod"
        @load="loadShipments"
        @cancel="cancelLoad"
      />
      <!-- A shipment opens over the tab it was opened from: the shipment list or a customer's review. -->
      <ShipmentDetail
        v-if="selectedShipment"
        :entry="selectedShipment"
        :quote-billing="selectedQuoteBilling"
        :quote-eligible="financialQuoteEligible"
        :blocker="detailBlocker"
        :price="lineSuggestion"
        :back-label="tab !== 'customers' ? 'Back to shipments' : groupRow ? `Back to ${groupRow.label}` : 'Back to customers'"
        :busy="busy"
        :notice="message"
        :failure="error"
        @back="closeShipment"
        @review="reviewCustomer"
        @open-line="openLineFromShipment"
        @link="linkShipment"
        @go-billing="billCustomer"
      />
      <!-- The ledger stays mounted so its period, filters, page and selection survive details and tab changes. -->
      <div v-show="tab === 'shipments'" id="financial-panel-shipments" role="tabpanel" aria-labelledby="financial-tab-shipments">
        <ShipmentLedger
          v-show="!selectedShipment"
          :entries="ledger"
          :period="bookingPeriod"
          :without-shipment="withoutShipment"
          :eligible="eligibleCount"
          :reasons="entryReasons"
          :busy="busy || ledgerLoading"
          :loading="ledgerLoading"
          :opening="loading"
          :progress="ledgerProgress"
          :result="ledgerResult"
          :loaded-periods="loadedPeriods"
          @open="openShipment"
          @open-line="openOrphan"
          @go-invoices="goInvoices"
        />
      </div>
      <!-- One page per customer: the list, then the review with its grid, approval and billing. -->
      <div v-show="tab === 'customers'" id="financial-panel-customers" role="tabpanel" aria-labelledby="financial-tab-customers">
        <div v-if="!selectedGroup && !selectedShipment" class="financial__customer-scope">
          <div class="financial__scope-choices" role="group" aria-label="Customers to show">
            <button class="financial__button" type="button" :aria-pressed="customerScope === 'booked'" @click="showBookedCustomers">Booked in period</button>
            <button class="financial__button" type="button" :aria-pressed="customerScope === 'invoices'" @click="showInvoiceCustomers">All invoice customers</button>
          </div>
          <p role="status">{{ customerScopeLabel }}</p>
          <p v-if="customerScope === 'booked'" class="financial__help">An invoice can contain older bookings. Choose All invoice customers to compare every customer with your invoice reports.</p>
          <button v-if="customerScope === 'booked' && (olderEntries.length || showOlder)" class="financial__link" type="button" @click="toggleOlder">
            {{ showOlder ? 'Back to selected booking period' : `Older work still to review or bill (${numberLabel(olderEntries.length)} shipments)` }}
          </button>
        </div>
        <ApprovalOverview
          v-show="!selectedGroup && !selectedShipment"
          :customers="overviewCustomers"
          :unmatched="withoutShipment.length"
          :empty-text="customerEmptyText"
          @review="openGroup"
          @go-invoices="goInvoices"
          @go-shipments="goShipments"
        />
        <GroupReview
          v-if="selectedGroup && groupRow"
          v-show="!selectedShipment"
          :title="groupRow.label"
          :group="groupRow"
          :review="groupData!"
          :busy="busy"
          :notice="message"
          :failure="error"
          :currency="groupRow.currency"
          :grid-rows="gridRows"
          :save="gridSave"
          :can-undo="undoStack.length > 0"
          :can-redo="redoStack.length > 0"
          :imports="imports"
          :entries="customerEntries"
          :billing-entries="ledger"
          :quotes="quotes"
          :period="billingPeriod"
          :state="stateOf"
          :billing-notice="billingNotice"
          :billing-failure="billingFailure"
          @edit="editGrid"
          @undo="undoGrid"
          @redo="redoGrid"
          @retry="retryGrid"
          @back="closeGroup"
          @approve="approveSelectedGroup"
          @open-shipment="openShipment"
          @select="rememberBilling"
          @export="exportCustomer"
        />
      </div>
      <div v-if="tab === 'invoices'" id="financial-panel-invoices" role="tabpanel" aria-labelledby="financial-tab-invoices">
        <p v-if="!selectedRow" class="financial__help">All imported invoices. The booking period in Shipments and Customers does not filter this view.</p>
        <template v-if="!active">
          <!-- The whole panel is the drop target, so the imports below stay in the first view. -->
          <section
            class="financial__panel financial__upload"
            :class="{ 'financial__upload--dragging': dragging }"
            aria-labelledby="financial-upload-title"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop"
          >
            <h2 id="financial-upload-title">Add a carrier invoice</h2>
            <div class="financial__upload-row">
              <input
                id="financial-file"
                ref="fileInput"
                class="financial__file-input"
                type="file"
                accept=".xlsx,.csv,.tsv"
                :disabled="busy || loading"
                :aria-invalid="!!error"
                :aria-describedby="error ? 'financial-error' : 'financial-upload-hint'"
                @change="onFileChange"
              />
              <label
                class="financial__file-button"
                :class="{ 'financial__file-button--disabled': busy || loading }"
                for="financial-file"
              >
                <mdicon name="file-upload-outline" :size="18" aria-hidden="true" /> Choose file
              </label>
              <p id="financial-upload-hint" class="financial__upload-hint" aria-live="polite">{{ uploadHint }}</p>
            </div>
          </section>
          <section class="financial__panel financial__history">
            <div class="financial__report-heading">
              <div>
                <h2 id="financial-imports-title" tabindex="-1">Your imports</h2>
                <p id="financial-combined-hint" class="financial__help">One Excel report with matched and unmatched rows from the selected invoices, across all carriers. The booking period does not limit this report.</p>
              </div>
              <button id="financial-export-combined" class="financial__button financial__button--primary" type="button" :disabled="busy || loading || matching || !reportLines.length" aria-describedby="financial-combined-hint" @click="exportCombinedReview">
                <mdicon name="download" :size="18" aria-hidden="true" /> Download combined report
              </button>
            </div>
            <p v-if="imports.length" role="status">{{ reportImports.length }} of {{ imports.length }} invoices selected · {{ numberLabel(reportLines.length) }} rows · {{ reportMatchedCount }} matched · {{ reportLines.length - reportMatchedCount }} not matched</p>
            <p v-if="loading" class="financial__empty" role="status">Opening your workspace…</p>
            <div v-else-if="!imports.length" class="financial__empty">
              <mdicon name="file-document-multiple-outline" :size="32" aria-hidden="true" /><strong>No invoices yet</strong>
              <p>Add a carrier file to start matching costs to customers.</p>
            </div>
            <div v-else class="financial__table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col" class="financial__report-select"><input type="checkbox" aria-label="Select all invoices for combined report" :checked="reportImports.length === imports.length" :indeterminate="reportImports.length > 0 && reportImports.length < imports.length" :disabled="busy" @change="toggleAllReportInvoices" /></th>
                    <th scope="col">Invoice</th>
                    <th scope="col">Imported</th>
                    <th scope="col" class="financial__numeric">Rows</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- The invoice's name opens it; an import is deleted from its own header, after it has been opened. -->
                  <tr v-for="batch in imports" :key="batch.id">
                    <td class="financial__report-select"><input type="checkbox" :aria-label="`Include ${batch.carrier} ${batch.invoiceNumber || batch.fileName} in combined report`" :checked="!excludedReportInvoices.includes(batch.id)" :disabled="busy" @change="toggleReportInvoice(batch.id)" /></td>
                    <td>
                      <button
                        class="financial__link"
                        type="button"
                        :disabled="busy"
                        :aria-describedby="`financial-import-file-${batch.id}`"
                        @click="openFromList(batch)"
                      >
                        {{ batch.carrier }} {{ batch.invoiceNumber || 'Invoice details needed' }}
                      </button>
                      <small :id="`financial-import-file-${batch.id}`">{{ batch.fileName }}</small>
                    </td>
                    <td>{{ dateLabel(batch.createdAt) }}<small>by {{ batch.createdByName }}</small></td>
                    <td class="financial__numeric">{{ numberLabel(batch.charges.length) }}</td>
                    <td>
                      <span class="financial__badge" :class="`financial__badge--${importStatus(batch).tone}`">{{ importStatus(batch).label }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </template>
        <template v-else>
          <section v-show="!selectedRow || invoiceDetailsVisible" class="financial__panel financial__batch">
            <template v-if="!selectedRow">
              <!-- One header: which file this is, when and by whom it was imported, and the invoice's downloads. -->
              <header class="financial__invoice-head">
                <div>
                  <button class="financial__back" type="button" :disabled="busy" @click="backToImports">
                    <mdicon name="arrow-left" aria-hidden="true" /> All imports
                  </button>
                  <h2 id="financial-invoice-title" tabindex="-1">{{ active.carrier }} {{ invoiceLabel }}</h2>
                  <p class="financial__help">{{ invoiceMeta }}</p>
                </div>
                <div class="financial__actions">
                  <button class="financial__button" type="button" :disabled="busy" @click="downloadOriginal">
                    <mdicon name="download" aria-hidden="true" /> Original file
                  </button>
                  <button class="financial__button" type="button" :disabled="busy || matching || !exportRows.length" aria-describedby="financial-review-export-hint" @click="exportReview">
                    <mdicon name="download" aria-hidden="true" /> Review report
                  </button>
                  <button
                    id="financial-export-billing"
                    class="financial__button"
                    type="button"
                    :disabled="busy || !exportReady.length"
                    @click="exportBilling"
                  >
                    <mdicon name="download" aria-hidden="true" /> Export billing basis · {{ exportCustomerLabel }}
                  </button>
                  <button
                    id="financial-delete-import"
                    class="financial__button financial__button--quiet financial__invoice-delete"
                    type="button"
                    :disabled="busy"
                    @click="askDelete(active.id)"
                  >
                    <mdicon name="delete-outline" :size="18" aria-hidden="true" /> Delete import…
                  </button>
                </div>
              </header>
              <p id="financial-review-export-hint" class="financial__help" role="status">
                {{ reviewExportHint }}
              </p>
              <div
                v-if="deleteCandidate === active.id"
                class="financial__confirm"
                role="alertdialog"
                aria-labelledby="financial-delete-title"
                aria-describedby="financial-delete-text"
              >
                <div>
                  <strong id="financial-delete-title">Delete the import of {{ active.fileName }}?</strong>
                  <p id="financial-delete-text">
                    This removes the saved original and all review decisions in this browser.
                    {{ active.exports.length ? `${active.exports.length} recorded downloads are removed with it; files already downloaded are not affected.` : '' }}
                  </p>
                </div>
                <div class="financial__actions">
                  <button id="financial-delete-confirm" class="financial__button financial__button--danger" type="button" :disabled="busy" @click="deleteImport(active.id)">
                    Delete import
                  </button>
                  <button class="financial__button" type="button" :disabled="busy" @click="cancelDelete">Cancel</button>
                </div>
              </div>
              <div v-if="exportPrompt" class="financial__confirm" role="alertdialog" aria-labelledby="financial-reexport-title" aria-describedby="financial-reexport-text">
                <div>
                  <strong id="financial-reexport-title">{{ exportPrompt.billed }} of these rows were already billed</strong>
                  <p id="financial-reexport-text">{{ exportPrompt.last }}. Exporting them again can bill the customer twice.</p>
                </div>
                <div class="financial__actions">
                  <button
                    v-if="exportPrompt.fresh"
                    id="financial-reexport-new"
                    class="financial__button financial__button--primary"
                    type="button"
                    :disabled="busy"
                    @click="confirmExport(false)"
                  >
                    Export the {{ exportPrompt.fresh }} new rows
                  </button>
                  <button id="financial-reexport-include" class="financial__button" type="button" :disabled="busy" @click="confirmExport(true)">
                    Include billed rows again
                  </button>
                  <button class="financial__button" type="button" :disabled="busy" @click="cancelExport">Cancel</button>
                </div>
              </div>
            </template>
            <!-- Hidden but kept while a row is open, like the costs below. -->
            <p v-show="!selectedRow" class="financial__export">{{ lastDownload }}</p>
            <template v-if="!selectedRow">
              <div class="financial__metrics financial__metrics--three">
                <div>
                  <span>Matched to a customer</span>
                  <strong>{{ numberLabel(matchedCount) }} <small>/ {{ numberLabel(active.charges.length) }}</small></strong>
                  <small>{{ matchedText }}</small>
                </div>
                <div>
                  <span>{{ active.carrier === 'PostNord' && !active.amountBasis ? 'Gross amount · provisional' : 'Invoice cost' }}</span>
                  <strong>{{ amountLabel(coverage.total) }}</strong>
                  <small v-if="coverage.total === null">Invalid source amounts need review</small>
                  <template v-else-if="reconciliation.length">
                    <!-- The invoice and control sheet totals against the imported rows: an icon and text, never colour alone. -->
                    <small v-for="check in reconciliation" :key="check.label" :class="{ financial__attention: !check.same }">
                      <mdicon :name="check.same ? 'check-circle-outline' : 'alert-outline'" :size="14" aria-hidden="true" />
                      {{ check.label }} {{ amountLabel(check.value, active.currency) }} ·
                      {{ check.same ? 'totals match' : `difference ${check.difference}` }}
                    </small>
                  </template>
                  <small v-else>{{ active.currency || 'Currency not confirmed' }}</small>
                </div>
                <div>
                  <span>Cost without approved revenue</span>
                  <strong :class="{ financial__attention: coverage.uncovered && Number(coverage.uncovered) !== 0 }">{{ amountLabel(coverage.uncovered) }}</strong>
                  <small>{{ numberLabel(coverage.pending + coverage.unresolved) }} open · {{ numberLabel(coverage.excluded) }} excluded</small>
                </div>
              </div>
              <div v-if="!active.matchedAt || matching" class="financial__match-callout">
                <div>
                  <strong>{{ matching ? 'Matching shipments…' : 'Find the customer behind each charge' }}</strong>
                  <p>
                    {{
                      matching
                        ? progress
                        : 'Match exact shipment numbers against PacSort. Shipments PacSort no longer returns are linked through earlier invoices in this workspace.'
                    }}
                  </p>
                </div>
                <!-- One button that stays in place, so focus stays on it when matching starts or is cancelled. -->
                <button
                  class="financial__button"
                  :class="{ 'financial__button--primary': !matching }"
                  type="button"
                  :disabled="busy && !matching"
                  @click="matching ? cancelMatching() : matchImport()"
                >
                  {{ matching ? 'Cancel matching' : 'Match shipments' }}
                  <mdicon v-if="!matching" name="arrow-right" aria-hidden="true" />
                </button>
              </div>
            </template>
            <button v-if="selectedRow" class="financial__back-link" type="button" @click="closeInvoiceDetails">
              <mdicon name="arrow-left" :size="18" aria-hidden="true" /> Back to charge
            </button>
            <!-- Opens by itself until the details are confirmed; approvals need them. -->
            <details class="financial__metadata" :open="!!selectedRow || !metadataComplete">
              <summary>
                Invoice details
                <template v-if="metadataComplete">
                  · {{ active.invoiceNumber }} · {{ active.currency }}<template v-if="totalsText"> · {{ totalsText }}</template>
                </template>
                <span v-else class="financial__badge financial__badge--review">Confirmation needed</span>
              </summary>
              <p class="financial__help">Copy these from the invoice PDF.</p>
              <form novalidate @submit.prevent="saveMetadata">
                <div class="financial__form-grid">
                  <div>
                    <label class="financial__label" for="financial-invoice">Invoice number</label><input
                      id="financial-invoice"
                      v-model="invoiceNumber"
                      class="form-control"
                      :disabled="busy"
                      maxlength="120"
                      required
                    />
                  </div>
                  <div>
                    <label class="financial__label" for="financial-currency">Invoice currency</label><select id="financial-currency" v-model="currency" class="form-select" :disabled="busy" required>
                      <option value="" disabled>Confirm currency</option>
                      <option v-for="option in currencies" :key="option">{{ option }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="financial__label" for="financial-total">Invoice total excl. VAT (optional)</label><input
                      id="financial-total"
                      v-model="invoiceTotal"
                      class="form-control"
                      inputmode="decimal"
                      autocomplete="off"
                      :disabled="busy"
                      aria-describedby="financial-total-hint"
                    />
                    <p id="financial-total-hint" class="financial__field-hint">Compared with the sum of the imported rows.</p>
                  </div>
                </div>
                <div v-if="active.carrier === 'PostNord'">
                  <label class="financial__label" for="financial-basis">How does the invoice total include the surcharge columns?</label><select
                    id="financial-basis"
                    v-model="amountBasis"
                    class="form-select"
                    :disabled="busy"
                    required
                    aria-describedby="financial-basis-hint"
                  >
                    <option value="" disabled>Check against the invoice total</option>
                    <option value="gross">Gross amount already includes them</option>
                    <option value="gross-plus-surcharges">Add surcharge columns to Gross amount</option>
                  </select>
                  <p id="financial-basis-hint" class="financial__field-hint">
                    The surcharge columns keep their original signs; the invoice total above checks the choice.
                  </p>
                </div>
                <p v-if="metadataError" class="financial__error" role="alert">{{ metadataError }}</p>
                <!-- Opened from a charge, the page's alert and the charge are hidden: a failed save is shown here. -->
                <p v-else-if="error && selectedRow" class="financial__error" role="alert">{{ error }}</p>
                <div class="financial__actions">
                  <button class="financial__button" :disabled="busy" type="submit">Save invoice details</button>
                  <span v-if="reopenCount" class="financial__attention" role="status">
                    <mdicon name="alert-outline" :size="16" aria-hidden="true" />
                    Saving reopens {{ reopenCount }} approved {{ reopenCount === 1 ? 'row' : 'rows' }}; the entered amounts are kept.
                  </span>
                </div>
              </form>
            </details>
          </section>
          <section v-show="!selectedRow" class="financial__panel financial__results">
            <div class="financial__panel-heading">
              <h2 id="financial-results-title" tabindex="-1">{{ view === 'customers' ? 'Costs by customer' : 'All charges' }}</h2>
              <div class="financial__actions">
                <button v-if="active.matchedAt" class="financial__button" :disabled="busy" @click="matchImport">
                  Refresh matches
                </button>
                <div class="financial__segmented">
                  <button :aria-pressed="view === 'customers'" @click="showCustomers">By customer</button><button :aria-pressed="view === 'charges'" @click="showCharges">All charges</button>
                </div>
              </div>
            </div>
            <template v-if="view === 'customers'">
              <div class="financial__table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Customer</th>
                      <th scope="col" class="financial__numeric">Rows</th>
                      <th scope="col" class="financial__numeric">Quoted price (SEK)</th>
                      <th scope="col" class="financial__numeric">Actual carrier cost {{ active.currency }}</th>
                      <th scope="col" class="financial__numeric">Approved revenue · margin</th>
                      <th scope="col" class="financial__numeric">Open</th>
                      <th scope="col"><span class="visually-hidden">Action</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="customer in customerSummaries" :key="customer.id">
                      <td>
                        <strong>{{ customer.name }}</strong><small v-if="customer.excluded">{{ customer.excluded }} excluded</small>
                      </td>
                      <td class="financial__numeric">
                        {{ numberLabel(customer.charges) }}<small>{{ numberLabel(customer.shipments) }} {{ customer.shipments === 1 ? 'shipment' : 'shipments' }}</small>
                      </td>
                      <td class="financial__numeric">{{ amountLabel(customer.quotedPrice ?? null, 'SEK') }}</td>
                      <td class="financial__numeric">{{ amountLabel(customer.amount) }}</td>
                      <td class="financial__numeric">
                        <template v-if="customer.reviewedRevenue === null">—</template>
                        <template v-else>
                          {{ amountLabel(customer.reviewedRevenue) }} ·
                          <span :class="{ financial__negative: isNegative(customer.reviewedMargin) }">{{ marginLabel(customer.reviewedMargin) }}</span>
                        </template>
                      </td>
                      <!-- Rows still to decide, and the cost that has no approved revenue yet, excluded rows included. -->
                      <td class="financial__numeric">
                        <span class="financial__badge" :class="{ 'financial__badge--review': customer.needsReview }">{{ customer.needsReview }}</span>
                        <small>{{ amountLabel(customer.uncoveredAmount) }} without revenue</small>
                      </td>
                      <td>
                        <button
                          class="financial__button financial__button--small"
                          type="button"
                          :aria-label="`Show charges of ${customer.name}`"
                          @click="openCustomer(customer.id)"
                        >
                          Show charges
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
            <template v-else>
              <div class="financial__filters">
                <div>
                  <label for="financial-filter">Show</label><select id="financial-filter" v-model="filter" class="form-select">
                    <optgroup label="Status">
                      <option value="all">All charges</option>
                      <option value="pending">Needs review</option>
                      <option value="unmatched">Missing match</option>
                      <option value="approved">Approved</option>
                      <option value="excluded">Excluded</option>
                      <option value="uncovered">Cost without approved revenue</option>
                      <option value="billed">Already billed</option>
                      <option value="draft">Unsaved drafts</option>
                    </optgroup>
                    <optgroup label="Differences">
                      <option value="heavier">Measured heavier than booked</option>
                      <option value="weight">Weight differs</option>
                      <option value="service">Service differs</option>
                      <option value="additional">Additional charges and credits</option>
                      <option value="shipment">Shipments with several rows</option>
                      <option value="warnings">Possible double billing</option>
                      <option value="negative">Negative margin</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label for="financial-customer">Customer</label><select id="financial-customer" v-model="customerId" class="form-select">
                    <option value="all">All customers</option>
                    <option v-for="customer in customerSummaries" :key="customer.id" :value="customer.id">
                      {{ customer.name }}
                    </option>
                  </select>
                </div>
                <div class="financial__search">
                  <label for="financial-search">Find a charge</label><input
                    id="financial-search"
                    v-model="search"
                    class="form-control"
                    placeholder="Tracking number, reference or service"
                    type="search"
                  />
                </div>
              </div>
              <BulkReview
                v-if="selection.length"
                :count="selectedRows.length"
                :select-all="selectAllCount"
                :busy="busy"
                @exclude="bulkExclude"
                @select-all="selectAllFiltered"
                @clear="clearSelection"
              />
              <div class="financial__table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th scope="col" class="financial__select-cell">
                        <input
                          id="financial-select-page"
                          type="checkbox"
                          :checked="allOnPageSelected"
                          :disabled="!pageRows.length"
                          aria-label="Select all rows on this page"
                          @change="togglePage"
                        />
                      </th>
                      <th scope="col">Shipment / reference</th>
                      <th scope="col">Customer</th>
                      <th scope="col">Charge</th>
                      <th scope="col">Quoted shipment price (SEK)</th>
                      <th scope="col">Actual carrier cost {{ active.currency }}</th>
                      <th scope="col">Weight</th>
                      <th scope="col">Status</th>
                      <th scope="col">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in pageRows"
                      :key="row.id"
                      :class="{ 'financial__row--selected': selectedId === row.id || selectionSet.has(row.id) }"
                    >
                      <td class="financial__select-cell">
                        <input
                          type="checkbox"
                          :checked="selectionSet.has(row.id)"
                          :aria-label="`Select ${row.trackingNumber || row.id}`"
                          @change="toggleRow(row.id)"
                        />
                      </td>
                      <td>
                        <strong>{{ row.trackingNumber || 'No shipment number' }}</strong><small>{{ row.reference || `${row.sheet} · row ${row.rowNumber}` }}</small>
                      </td>
                      <td>{{ billingName(row.match) }}</td>
                      <td>
                        {{ row.description }}
                        <span v-if="signalsOf(row).role !== 'base'" class="financial__tag">{{ signalsOf(row).role === 'credit' ? 'Credit' : 'Additional' }}</span>
                        <small>{{ row.date }}</small>
                      </td>
                      <td class="financial__numeric">{{ amountLabel(row.match?.bookedPrice ?? null, 'SEK') }}</td>
                      <td class="financial__numeric" :title="chargeAmount(active, row) ?? 'Invalid amount'">
                        {{ amountLabel(chargeAmount(active, row)) }}
                      </td>
                      <td>
                        {{ signalsOf(row).weight.invoice }}
                        <small :class="{ 'financial__attention': signalsOf(row).weight.state === 'different' }">
                          <mdicon v-if="signalsOf(row).weight.state === 'different'" name="alert-outline" :size="14" aria-hidden="true" />
                          {{ weightNote(row) }}
                        </small>
                      </td>
                      <td>
                        <span
                          class="financial__badge"
                          :class="{
                            'financial__badge--review': !ready(active, row) && row.review?.status !== 'excluded',
                            'financial__badge--ready': ready(active, row),
                          }"
                        >{{ statusLabel(row) }}</span>
                        <small v-if="rowHint(row)">{{ rowHint(row) }}</small>
                      </td>
                      <td>
                        <button
                          class="financial__button"
                          :disabled="busy"
                          :aria-label="`Open row ${row.trackingNumber || row.id}`"
                          @click="selectCharge(row.id, $event)"
                        >
                          Open row
                        </button>
                      </td>
                    </tr>
                    <tr v-if="!pageRows.length">
                      <td colspan="8" class="financial__empty">
                        No charges match these filters. Try a different customer, filter or search.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="financial__pagination">
                <span>{{ numberLabel(filteredRows.length) }} charges · page {{ page }} of {{ pageCount }}</span>
                <div class="financial__actions">
                  <button class="financial__button" :disabled="page <= 1" @click="previousPage">Previous</button><button class="financial__button" :disabled="page >= pageCount" @click="nextPage">Next</button>
                </div>
              </div>
            </template>
          </section>
          <!-- Hidden, not removed, while its invoice details are open: unsaved inputs survive the round trip. -->
          <ChargeReview
            v-if="selectedRow && selectedSignals"
            v-show="!invoiceDetailsVisible"
            :batch="active"
            :row="selectedRow"
            :signals="selectedSignals"
            :mappings="mappings"
            :draft="drafts[draftKey(selectedRow.id)] ?? null"
            :busy="busy"
            :suggestion="selectedSuggestion"
            :customer-margin="selectedMargin"
            :unpriced-text="selectedUnpriced"
            :has-next="!returnTarget && !!nextReviewId"
            :back-label="returnTarget === 'shipment' ? 'Back to shipment' : returnTarget === 'ledger' ? 'Back to shipments' : 'Back to charges'"
            :notice="message"
            :failure="error"
            @close="closeReview"
            @review="saveReview"
            @invoice-details="openInvoiceDetails"
            @draft="saveDraft"
            @confirm-mapping="confirmMapping"
            @open-entry="openEntry"
          />
        </template>
      </div>
    </template>
    <div v-else class="financial__panel" role="alert">
      <h1>Financial</h1>
      <p>This workspace is for internal staff only.</p>
    </div>
  </main>
</template>
<script lang="ts">
import type { FinancialActiveCarrier } from '@/const/Financial'
import type { FinancialInvoiceTabState } from '@/types/Components/FinancialInvoices'
import type { FinancialWorkspaceReconciliation, FinancialWorkspaceTabs, FinancialCustomerScope } from '@/types/Components/FinancialViewState'
import type { FinancialCustomerMargin } from '@/types/Models/Financial'
import type {
  FinancialBillingSelection,
  FinancialQuoteBilling,
  FinancialCharge,
  FinancialCustomerSummary,
  FinancialExportKind,
  FinancialExportRecord,
  FinancialImport,
  FinancialAdjustment,
  FinancialGridEdit,
  FinancialGridRow,
  FinancialLedgerEntry,
  FinancialLine,
  FinancialLineState,
  FinancialBookingPeriod,
  FinancialLoadedPeriod,
  FinancialMarginTable,
  FinancialOverviewRow,
  FinancialPriceSource,
  FinancialPriceSuggestion,
  FinancialReview,
  FinancialReviewDraft,
  FinancialReviewEvent,
  FinancialReviewStatus,
  FinancialReviewSubmission,
  FinancialRowSignals,
  FinancialServiceMapping,
  FinancialShipmentEntry,
  FinancialShipmentRecord,
} from '@/types/Models/Financial'
import {
  computed,
  defineComponent,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type ComputedRef,
  type Ref,
} from 'vue'
import { matchedRouteKey, onBeforeRouteLeave } from 'vue-router'
import { FINANCIAL_CURRENCIES, FINANCIAL_PAGE_SIZE } from '@/const/Financial'
import {
  financialAmountLabel,
  financialAmountsEqual,
  financialDecimal,
  financialDifferenceLabel,
  financialPercent,
  roundFinancialAmount,
  subtractFinancialAmounts,
  sumFinancialAmounts,
} from '@/services/FinancialAmounts'
import { financialBillingKey, financialBillingPlan, financialQuoteCandidates } from '@/services/FinancialBilling'
import { financialServiceMappingId } from '@/services/FinancialComparison'
import { downloadFinancialFile, financialExportFileName, financialExportWorkbook } from '@/services/FinancialExport'
import { readFinancialFile } from '@/services/FinancialFilePreview'
import { financialGroupReview } from '@/services/FinancialGroupReview'
import {
  financialBillingId,
  financialBillingName,
  financialChargeAmount,
  financialImportCarrier,
  financialInvoiceLabel,
  financialLocalDate,
  financialMetadataComplete,
  financialReady,
  financialRole,
  financialSameInvoiceNumber,
  normalizeFinancialImport,
} from '@/services/FinancialImport'
import {
  financialApplySplit,
  financialCostsWithoutShipment,
  financialKnownSplit,
  financialLedger,
  financialMatchFromRecord,
  financialMergeShipments,
  loadFinancialShipments,
} from '@/services/FinancialLedger'
import { financialReopenReview, matchFinancialImport } from '@/services/FinancialMatching'
import { financialBookingEntries } from '@/services/FinancialPeriod'
import {
  financialCustomerMargin,
  financialOversizeInBooking,
  financialPriceSuggestion,
  financialRowPrice,
  financialSizeUnknown,
  financialUnpricedReason,
  loadFinancialMargins,
} from '@/services/FinancialPricing'
import {
  financialBillingState,
  financialBulkBlocker,
  financialRowSignals,
  financialShipmentEntries,
  financialShipmentIndex,
} from '@/services/FinancialSignals'
import {
  financialCoverage,
  financialCustomerLines,
  financialCustomers,
  financialImportStatus,
  financialQuotedTotal,
} from '@/services/FinancialSummary'
import {
  deleteFinancialImport,
  financialFileHash,
  financialOriginal,
  listFinancialImports,
  listFinancialMappings,
  listFinancialShipments,
  listFinancialQuotes,
  saveFinancialBilling,
  saveFinancialImports,
  saveFinancialMapping,
  saveFinancialShipments,
} from '@/services/FinancialWorkspace'
import { useAccountStore } from '@/store/account'
import BulkReview from '@/views/Staff/Financial/Components/BulkReview.vue'
import GroupReview from '@/views/Staff/Financial/Components/GroupReview.vue'
import ChargeReview from '@/views/Staff/Financial/Components/ChargeReview.vue'
import BookingPeriod from '@/views/Staff/Financial/Components/BookingPeriod.vue'
import ShipmentDetail from '@/views/Staff/Financial/Components/ShipmentDetail.vue'
import ShipmentLedger from '@/views/Staff/Financial/Components/ShipmentLedger.vue'
import ApprovalOverview from '@/views/Staff/Financial/Components/ApprovalOverview.vue'

export default defineComponent({
  name: 'FinancialIndex',
  components: { BookingPeriod, ApprovalOverview, BulkReview, ChargeReview, GroupReview, ShipmentDetail, ShipmentLedger },
  setup() {
    const account = useAccountStore()
    const canAccess: ComputedRef<boolean> = computed(() => account.ability.can('view', 'financial'))
    const owner: ComputedRef<string> = computed(() => account.userId ?? '')
    const userName: ComputedRef<string> = computed(
      () => [account.account.firstName, account.account.lastName].filter(Boolean).join(' ') || 'Staff',
    )
    const imports: Ref<FinancialImport[]> = ref([])
    const active: Ref<FinancialImport | null> = ref(null)
    const mappings: Ref<FinancialServiceMapping[]> = ref([])
    const busy: Ref<boolean> = ref(false)
    const loading: Ref<boolean> = ref(false)
    const matching: Ref<boolean> = ref(false)
    const dragging: Ref<boolean> = ref(false)
    const progress: Ref<string> = ref('')
    const message: Ref<string> = ref('')
    const error: Ref<string> = ref('')
    const metadataError: Ref<string> = ref('')
    const fileInput: Ref<HTMLInputElement | null> = ref(null)
    const invoiceNumber: Ref<string> = ref('')
    const currency: Ref<string> = ref('')
    const amountBasis: Ref<FinancialImport['amountBasis']> = ref('')
    const invoiceTotal: Ref<string> = ref('')
    const view: Ref<FinancialInvoiceTabState['view']> = ref('customers')
    const filter: Ref<string> = ref('all')
    const customerId: Ref<string> = ref('all')
    const search: Ref<string> = ref('')
    const page: Ref<number> = ref(1)
    const selectedId: Ref<string> = ref('')
    const invoiceDetailsVisible: Ref<boolean> = ref(false)
    const selection: Ref<string[]> = ref([])
    const drafts: Ref<Record<string, FinancialReviewDraft>> = ref({})
    const deleteCandidate: Ref<string> = ref('')
    // The billed-again prompt of the invoice's billing basis export.
    const exportPrompt: Ref<{ fresh: number; billed: number; last: string } | null> = ref(null)
    const tab: Ref<'shipments' | 'invoices' | 'customers'> = ref('shipments')
    const bookingPeriod: Ref<FinancialBookingPeriod> = ref({ from: '', to: '', carriers: [] })
    const customerScope: Ref<FinancialCustomerScope> = ref('booked')
    const showBookedCustomers = (): void => { customerScope.value = 'booked'; showOlder.value = false }
    const showInvoiceCustomers = (): void => { customerScope.value = 'invoices'; showOlder.value = false }
    const excludedReportInvoices: Ref<string[]> = ref([])
    const reportImports: ComputedRef<FinancialImport[]> = computed(() => imports.value.filter((batch) => !excludedReportInvoices.value.includes(batch.id)))
    const reportLines: ComputedRef<FinancialLine[]> = computed(() => reportImports.value.flatMap((batch) => batch.charges.map((row) => ({ batch, row }))))
    const reportMatchedCount: ComputedRef<number> = computed(() => reportLines.value.filter(({ row }) => row.matchStatus === 'matched' && !!row.match).length)
    const toggleReportInvoice = (id: string): void => {
      excludedReportInvoices.value = excludedReportInvoices.value.includes(id)
        ? excludedReportInvoices.value.filter((value) => value !== id) : [...excludedReportInvoices.value, id]
    }
    const toggleAllReportInvoices = (): void => {
      excludedReportInvoices.value = reportImports.value.length === imports.value.length ? imports.value.map((batch) => batch.id) : []
    }
    const showOlder: Ref<boolean> = ref(false)
    const changeBookingPeriod = (period: FinancialBookingPeriod): void => {
      bookingPeriod.value = period
      showOlder.value = false
    }
    const toggleOlder = (): void => { showOlder.value = !showOlder.value }
    const shipments: Ref<FinancialShipmentRecord[]> = ref([])
    const quotes: Ref<FinancialQuoteBilling[]> = ref([])
    const ledgerLoading: Ref<boolean> = ref(false)
    const ledgerProgress: Ref<string> = ref('')
    // What the last load from PacSort did, shown next to the period instead of at the top of the page.
    const ledgerResult: Ref<string> = ref('')
    const selectedShipmentId: Ref<string> = ref('')
    // Periods loaded from PacSort, remembered in this browser so the page knows which lists are complete.
    // Scoped like the workspace database, and only trusted while the shipments they loaded are still saved.
    const loadedPeriods: Ref<FinancialLoadedPeriod[]> = ref([])
    const periodsKey = (): string => `financial-loaded-periods:${import.meta.env.VITE_APP_API_LOCATION}:${owner.value}`
    const readLoadedPeriods = (known: FinancialShipmentRecord[]): FinancialLoadedPeriod[] => {
      try {
        const saved = JSON.parse(localStorage.getItem(periodsKey()) ?? '[]')
        const booked = known.filter((record) => record.source === 'pacsort').map((record) => financialLocalDate(record.bookedAt))
        return (Array.isArray(saved) ? (saved as FinancialLoadedPeriod[]) : []).filter(
          (period) => !period.count || booked.some((day) => day >= period.from && day <= period.to),
        )
      } catch {
        return []
      }
    }
    const rememberPeriod = (period: FinancialLoadedPeriod): void => {
      loadedPeriods.value = [...loadedPeriods.value.slice(-49), period]
      try {
        localStorage.setItem(periodsKey(), JSON.stringify(loadedPeriods.value))
      } catch {
        // Browser storage can be unavailable; the page then asks PacSort again next time.
      }
    }
    // The billing period of the customer page's Bill section, kept from one customer to the next.
    const billingPeriod: Ref<Omit<FinancialBillingSelection, 'customerId'>> = ref({ from: '', to: '', onlyNew: true })
    // What the last customer export did, or why it failed, shown next to its buttons.
    const billingNotice: Ref<string> = ref('')
    const billingFailure: Ref<string> = ref('')
    // Where closing a charge returns to when it was opened from the shipment ledger.
    const returnTarget: Ref<'shipment' | 'ledger' | ''> = ref('')
    // Customer margins per company and carrier (Customers › Cost margins), loaded for the customers in the workspace.
    const margins: Ref<FinancialMarginTable> = ref({})
    const marginNotice: Ref<string> = ref('')
    const marginLoading: Ref<boolean> = ref(false)
    // The customer being reviewed before approval: the only place rows are approved together.
    const selectedGroup: Ref<{ id: string } | null> = ref(null)
    // Review grid changes not saved yet (key → adjustment, null removes it), and undo/redo of grid edits.
    const pendingAdjustments: Ref<Map<string, FinancialAdjustment | null>> = ref(new Map())
    const undoStack: Ref<{ key: string; before: FinancialAdjustment | null; after: FinancialAdjustment | null }[][]> = ref([])
    const redoStack: Ref<{ key: string; before: FinancialAdjustment | null; after: FinancialAdjustment | null }[][]> = ref([])
    const gridSave: Ref<'idle' | 'saving' | 'saved' | 'error'> = ref('idle')
    let gridTimer = 0
    let gridChain: Promise<void> = Promise.resolve()
    let marginController: AbortController | null = null
    let returnToShipment = ''
    // The tab a shipment was opened from, so a charge opened from it returns there too.
    let returnTab: 'shipments' | 'customers' = 'shipments'
    // The label of the button that opened a charge from the shipment ledger or a shipment, which gets focus back.
    let returnFocus = ''
    // What the Carrier invoices tab showed before a charge from another tab was opened in it.
    let invoiceTab: FinancialInvoiceTabState | null = null
    let session = 0
    let matchController: AbortController | null = null
    let ledgerController: AbortController | null = null
    let reviewTrigger: HTMLElement | null = null
    // The control that opened a charge's invoice details, which gets focus back when they close.
    let invoiceDetailsTrigger: HTMLElement | null = null

    const shipmentIndex: ComputedRef<ReturnType<typeof financialShipmentIndex>> = computed(() => financialShipmentIndex(imports.value))
    // The base: everything PacSort shipped, filled with carrier costs from every imported invoice.
    const ledger: ComputedRef<FinancialLedgerEntry[]> = computed(() =>
      financialLedger(shipments.value, imports.value, margins.value),
    )
    // The price a row is billed at: the reviewer's grid price, otherwise PacSort's price.
    const suggestionOf = (batch: FinancialImport, row: FinancialCharge): FinancialPriceSuggestion | null =>
      financialRowPrice(batch, row, margins.value)
    const entryByItem: ComputedRef<Map<string, FinancialLedgerEntry>> = computed(() => new Map(ledger.value.map((entry) => [entry.shipment.itemId, entry])))
    // Rows approved together in the customer's review use each row's price (PacSort's, or one set in the grid), so a
    // row without one is reviewed individually. A delivery is billed once per shipment, also across shipment numbers.
    const bulkBlockerOf = (batch: FinancialImport, row: FinancialCharge, signals: FinancialRowSignals): string | null => {
      const blocker = financialBulkBlocker(batch, row, signals)
      if (blocker) return blocker
      if (row.adjustment?.exclude) return 'Marked not to bill in the review'
      if (signals.role === 'base' && (entryByItem.value.get(row.match!.itemId)?.deliveries ?? 0) > 1)
        return 'Several delivery rows for this shipment'
      if (financialOversizeInBooking(batch, row)) return 'Oversize may already be in the booked price'
      if (financialSizeUnknown(batch, row)) return 'Booked size not loaded; refresh matches'
      if (signals.role === 'base' && row.match?.bookedPrice != null && batch.currency && batch.currency !== 'SEK')
        return 'Booked price is in SEK'
      return suggestionOf(batch, row) ? null : financialUnpricedReason(batch, row, margins.value)
    }
    const withoutShipment: ComputedRef<FinancialLine[]> = computed(() =>
      financialCostsWithoutShipment(imports.value, ledger.value),
    )
    const selectedShipment: ComputedRef<FinancialLedgerEntry | undefined> = computed(() =>
      selectedShipmentId.value ? ledger.value.find((entry) => entry.shipment.itemId === selectedShipmentId.value) : undefined,
    )
    const inDetail: ComputedRef<boolean> = computed(
      () =>
        !!selectedShipment.value ||
        (tab.value === 'customers' && !!selectedGroup.value) ||
        (tab.value === 'invoices' && !!selectedRow.value),
    )
    const tabs: ComputedRef<FinancialWorkspaceTabs> = computed(() => [
      // No count: the list depends on the chosen period, and the coverage above it counts that.
      { id: 'shipments' as const, label: 'Shipments', icon: 'truck-outline', count: null },
      { id: 'invoices' as const, label: 'Carrier invoices', icon: 'file-table-outline', count: imports.value.length },
      // Include completed customers too; the count follows the selected customer scope.
      {
        id: 'customers' as const,
        label: 'Customers',
        icon: 'account-multiple-outline',
        count: overviewCustomers.value.length,
      },
    ])
    const signalMap: ComputedRef<Map<string, FinancialRowSignals>> = computed(() => {
      const batch = active.value
      if (!batch) return new Map()
      return new Map(batch.charges.map((row) => [row.id, financialRowSignals(batch, row, shipmentIndex.value, mappings.value)]))
    })
    const signalsOf = (row: FinancialCharge): FinancialRowSignals => signalMap.value.get(row.id)!
    const customerSummaries: ComputedRef<FinancialCustomerSummary[]> = computed(() =>
      active.value ? financialCustomers(active.value) : [],
    )
    const coverage: ComputedRef<ReturnType<typeof financialCoverage>> = computed(() =>
      active.value
        ? financialCoverage(active.value)
        : { total: null, covered: '0', uncovered: null, ready: 0, pending: 0, excluded: 0, unresolved: 0 },
    )
    const matchedCount: ComputedRef<number> = computed(
      () => active.value?.charges.filter((row) => row.matchStatus === 'matched').length ?? 0,
    )
    const metadataComplete: ComputedRef<boolean> = computed(() => !!active.value && financialMetadataComplete(active.value))
    const invoiceLabel: ComputedRef<string> = computed(() => (active.value ? financialInvoiceLabel(active.value) : ''))
    // The header's meta line. The file name is left out when the heading already shows it for want of a number.
    const invoiceMeta: ComputedRef<string> = computed(() => {
      const batch = active.value
      if (!batch) return ''
      const imported = `${dateLabel(batch.createdAt)} by ${batch.createdByName}`
      return batch.invoiceNumber ? `${batch.fileName} · imported ${imported}` : `Imported ${imported}`
    })
    // The upload panel's one helper line, which also tells what is happening to a dropped or chosen file.
    const uploadHint: ComputedRef<string> = computed(() => {
      if (busy.value) return 'Reading and saving your file…'
      return dragging.value
        ? 'Drop the file to import it'
        : 'The original CityMail, Earlybird, PostNord or Deutsche Post file · XLSX, CSV or TSV · up to 20 MB'
    })
    const selectedRow: ComputedRef<FinancialCharge | undefined> = computed(() =>
      active.value?.charges.find((row) => row.id === selectedId.value),
    )
    const selectedSignals: ComputedRef<FinancialRowSignals | undefined> = computed(() =>
      selectedRow.value ? signalMap.value.get(selectedRow.value.id) : undefined,
    )
    const selectedSuggestion: ComputedRef<FinancialPriceSuggestion | null> = computed(() =>
      active.value && selectedRow.value ? suggestionOf(active.value, selectedRow.value) : null,
    )
    const selectedMargin: ComputedRef<FinancialCustomerMargin | null> = computed(() =>
      active.value && selectedRow.value?.match
        ? financialCustomerMargin(margins.value, selectedRow.value.match.companyId, active.value.carrier)
        : null,
    )
    const selectedUnpriced: ComputedRef<string> = computed(() =>
      active.value && selectedRow.value ? financialUnpricedReason(active.value, selectedRow.value, margins.value) : '',
    )
    const draftKey = (rowId: string): string => `${active.value?.id ?? ''}:${rowId}`
    const withoutDraft = (key: string): Record<string, FinancialReviewDraft> =>
      Object.fromEntries(Object.entries(drafts.value).filter(([draft]) => draft !== key))
    const isPending = (batch: FinancialImport, row: FinancialCharge): boolean =>
      !financialReady(batch, row) && row.review?.status !== 'excluded'
    const filters: Record<string, (row: FinancialCharge, signals: FinancialRowSignals, batch: FinancialImport) => boolean> = {
      all: () => true,
      pending: (row, _, batch) => isPending(batch, row),
      unmatched: (row) => row.matchStatus !== 'matched',
      approved: (row, _, batch) => financialReady(batch, row),
      excluded: (row) => row.review?.status === 'excluded',
      uncovered: (row, _, batch) => !financialReady(batch, row),
      billed: (_, signals) => signals.billed,
      draft: (row) => !!drafts.value[draftKey(row.id)],
      heavier: (_, signals) => (signals.weightDelta ?? 0) > 0,
      weight: (_, signals) => signals.weight.state === 'different',
      service: (_, signals) => signals.service.state === 'different',
      additional: (_, signals) => signals.role !== 'base',
      shipment: (_, signals) => signals.entries.some((entry) => entry.current),
      warnings: (_, signals) => signals.warnings.some((warning) => warning.blocking),
      negative: (_, signals) => signals.margin !== null && Number(signals.margin) < 0,
    }
    const filteredRows: ComputedRef<FinancialCharge[]> = computed(() => {
      const batch = active.value
      if (!batch) return []
      const text = search.value.trim().toLowerCase()
      const predicate = filters[filter.value] ?? filters.all
      return batch.charges.filter((row) => {
        if (customerId.value !== 'all' && financialBillingId(row.match) !== customerId.value) return false
        if (
          text &&
          ![row.trackingNumber, row.reference, row.description, financialBillingName(row.match)]
            .join(' ')
            .toLowerCase()
            .includes(text)
        )
          return false
        return predicate(row, signalsOf(row), batch)
      })
    })
    const pageCount: ComputedRef<number> = computed(() => Math.max(1, Math.ceil(filteredRows.value.length / FINANCIAL_PAGE_SIZE)))
    const pageRows: ComputedRef<FinancialCharge[]> = computed(() =>
      filteredRows.value.slice((page.value - 1) * FINANCIAL_PAGE_SIZE, page.value * FINANCIAL_PAGE_SIZE),
    )
    const selectionSet: ComputedRef<Set<string>> = computed(() => new Set(selection.value))
    const selectedRows: ComputedRef<FinancialCharge[]> = computed(
      () => active.value?.charges.filter((row) => selectionSet.value.has(row.id)) ?? [],
    )
    const allOnPageSelected: ComputedRef<boolean> = computed(
      () => !!pageRows.value.length && pageRows.value.every((row) => selectionSet.value.has(row.id)),
    )
    const nextReviewId: ComputedRef<string> = computed(() => {
      const rows = filteredRows.value
      const start = rows.findIndex((row) => row.id === selectedId.value)
      return (
        [...rows.slice(start + 1), ...rows.slice(0, Math.max(start, 0))].find(
          (row) => row.id !== selectedId.value && isPending(active.value!, row),
        )?.id ?? ''
      )
    })
    const exportRows: ComputedRef<FinancialCharge[]> = computed(
      () =>
        active.value?.charges.filter(
          (row) => customerId.value === 'all' || financialBillingId(row.match) === customerId.value,
        ) ?? [],
    )
    const exportMatched: ComputedRef<FinancialCharge[]> = computed(() =>
      exportRows.value.filter((row) => row.matchStatus === 'matched' && !!row.match),
    )
    const exportReady: ComputedRef<FinancialCharge[]> = computed(() =>
      exportRows.value.filter((row) => financialReady(active.value!, row)),
    )
    const exportFresh: ComputedRef<FinancialCharge[]> = computed(() =>
      exportReady.value.filter((row) => !signalsOf(row).billed),
    )
    const exportCustomerLabel: ComputedRef<string> = computed(() =>
      customerId.value === 'all'
        ? 'All customers'
        : (customerSummaries.value.find((customer) => customer.id === customerId.value)?.name ?? 'Unassigned'),
    )
    const reviewExportHint: ComputedRef<string> = computed(() =>
      `One report with all ${numberLabel(exportRows.value.length)} rows for ${exportCustomerLabel.value}: ${numberLabel(exportMatched.value.length)} matched and ${numberLabel(exportRows.value.length - exportMatched.value.length)} not matched. Use Match status in Excel to filter. Downloading does not approve or bill rows.`,
    )
    // One line instead of a download history: the latest download of this invoice, of any kind and from any page.
    const lastDownload: ComputedRef<string> = computed(() => {
      const records = active.value?.exports ?? []
      const last = records[records.length - 1]
      if (!last) return 'Last download: none yet'
      return `Last download: ${exportKindLabel(last.kind)} · ${last.customerName || 'All customers'} · ${last.count} rows · ${dateLabel(last.createdAt)} · ${last.userName}`
    })
    const reconciliation: ComputedRef<FinancialWorkspaceReconciliation> = computed(() => {
      const batch = active.value
      const total = coverage.value.total
      if (!batch) return []
      return [
        ...(batch.controlTotal ? [{ label: 'Control sheet total', value: batch.controlTotal }] : []),
        ...(batch.invoiceTotal ? [{ label: 'Invoice total', value: batch.invoiceTotal }] : []),
      ].map((check) => ({
        ...check,
        same: total !== null && financialAmountsEqual(total, check.value, 2),
        difference: total === null ? 'unknown' : financialDifferenceLabel(subtractFinancialAmounts(total, check.value), batch.currency),
      }))
    })
    // The confirmed details' summary adds the totals check once a total was compared.
    const totalsText: ComputedRef<string> = computed(() => {
      if (!reconciliation.value.length) return ''
      return reconciliation.value.every((check) => check.same) ? 'totals match' : 'totals differ'
    })
    const matchedText: ComputedRef<string> = computed(() => {
      if (!active.value?.matchedAt) return 'Not matched yet'
      const customers = customerSummaries.value.filter((customer) => customer.id).length
      return `${numberLabel(customers)} ${customers === 1 ? 'customer' : 'customers'} · ${coverage.value.unresolved} without a unique match`
    })
    // Saving a changed currency or amount basis reopens the approved rows; warned about only when that applies.
    const reopenCount: ComputedRef<number> = computed(() => {
      const batch = active.value
      if (!batch) return 0
      const changed =
        (!!batch.currency && batch.currency !== currency.value) ||
        (!!batch.amountBasis && batch.amountBasis !== amountBasis.value)
      return changed ? batch.charges.filter((row) => row.review?.status === 'approved').length : 0
    })
    // "Select all N rows in this list" in the selection bar, once every row on the page is selected.
    const selectAllCount: ComputedRef<number> = computed(() =>
      allOnPageSelected.value &&
      filteredRows.value.length > pageRows.value.length &&
      filteredRows.value.some((row) => !selectionSet.value.has(row.id))
        ? filteredRows.value.length
        : 0,
    )

    const reportError = (reason: unknown): void => {
      error.value =
        reason instanceof Error
          ? reason.message
          : 'The action could not be completed. Retry; your saved draft is unchanged.'
    }
    const dateLabel = (value: string): string => {
      return new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    }
    const numberLabel = (value: number): string => {
      return value.toLocaleString('en-GB')
    }
    const isNegative = (value: string | null): boolean => value !== null && Number(value) < 0
    const marginLabel = (value: string | null): string => (value === null ? '—' : financialDifferenceLabel(value))
    const importStatus = (batch: FinancialImport) => financialImportStatus(batch)
    const exportKindLabel = (kind: FinancialExportKind): string =>
      kind === 'billing' ? 'Billing basis' : kind === 'specification' ? 'Customer specification' : 'Review report'
    const resetFilters = (): void => {
      customerId.value = 'all'
      filter.value = 'all'
      search.value = ''
      page.value = 1
      selectedId.value = ''
      selection.value = []
      exportPrompt.value = null
      deleteCandidate.value = ''
      view.value = 'customers'
    }
    const openImport = (batch: FinancialImport): void => {
      active.value = batch
      invoiceNumber.value = batch.invoiceNumber
      currency.value = batch.currency
      amountBasis.value = batch.amountBasis
      invoiceTotal.value = batch.invoiceTotal ?? ''
      error.value = ''
      message.value = ''
      metadataError.value = ''
      resetFilters()
    }
    // The list or the upload panel is replaced by the invoice, so focus moves to its heading instead of being lost with
    // the clicked name or the file input.
    const focusInvoiceTitle = async (): Promise<void> => {
      await nextTick()
      document.getElementById('financial-invoice-title')?.focus()
    }
    const openFromList = async (batch: FinancialImport): Promise<void> => {
      openImport(batch)
      await focusInvoiceTitle()
    }
    // Back in the list, focus returns to the name of the invoice that was open, or to the list's heading.
    const backToImports = async (): Promise<void> => {
      const id = active.value?.id
      active.value = null
      message.value = ''
      error.value = ''
      resetFilters()
      await nextTick()
      const name = [...document.querySelectorAll<HTMLButtonElement>('.financial__history button')].find(
        (button) => button.getAttribute('aria-describedby') === `financial-import-file-${id}`,
      )
      if (name && !name.disabled) name.focus()
      else document.getElementById('financial-imports-title')?.focus()
    }
    // A charge opened from the Shipments or Customers tab opens its invoice in the Carrier invoices tab. What that tab
    // showed is kept, so closing the charge does not silently change the user's open invoice, filters or page.
    const rememberInvoiceTab = (): void => {
      invoiceTab = {
        importId: active.value?.id ?? null,
        view: view.value,
        filter: filter.value,
        customerId: customerId.value,
        search: search.value,
        page: page.value,
        selection: [...selection.value],
      }
    }
    const restoreInvoiceTab = async (saved: FinancialInvoiceTabState): Promise<void> => {
      const batch = imports.value.find((item) => item.id === saved.importId)
      if (!batch) {
        active.value = null
        resetFilters()
        return
      }
      if (batch.id !== active.value?.id) openImport(batch)
      view.value = saved.view
      filter.value = saved.filter
      customerId.value = saved.customerId
      search.value = saved.search
      selection.value = saved.selection
      // A changed filter or search first puts the list back on page 1. A row decided meanwhile can shorten the list.
      await nextTick()
      page.value = Math.min(saved.page, pageCount.value)
    }
    const loadWorkspace = async (): Promise<void> => {
      session += 1
      matchController?.abort()
      ledgerController?.abort()
      marginController?.abort()
      margins.value = {}
      marginNotice.value = ''
      imports.value = []
      excludedReportInvoices.value = []
      customerScope.value = 'booked'
      showOlder.value = false
      mappings.value = []
      shipments.value = []
      quotes.value = []
      loadedPeriods.value = []
      ledgerResult.value = ''
      selectedShipmentId.value = ''
      // A customer's page and its undo history belong to the workspace that was open.
      selectedGroup.value = null
      undoStack.value = []
      redoStack.value = []
      active.value = null
      busy.value = false
      matching.value = false
      error.value = ''
      message.value = ''
      if (!canAccess.value || !owner.value) return
      const version = session
      loading.value = true
      try {
        const [saved, confirmed, known, billedQuotes] = await Promise.all([
          listFinancialImports(owner.value),
          listFinancialMappings(owner.value),
          listFinancialShipments(owner.value),
          listFinancialQuotes(owner.value),
        ])
        if (version === session) {
          imports.value = saved
          mappings.value = confirmed
          shipments.value = known
          quotes.value = billedQuotes
          loadedPeriods.value = readLoadedPeriods(known)
          void refreshMargins()
        }
      } catch (reason) {
        if (version === session) reportError(reason)
      } finally {
        if (version === session) loading.value = false
      }
    }
    watch([canAccess, owner], loadWorkspace, { immediate: true })
    watch([filter, customerId, search], () => {
      page.value = 1
      selectedId.value = ''
    })
    // The billed-again prompt counts the rows of one customer scope; another scope asks again.
    watch(customerId, () => {
      exportPrompt.value = null
    })
    watch(pageCount, (count) => {
      if (page.value > count) page.value = count
    })
    const persist = async (batches: FinancialImport[], original?: File): Promise<void> => {
      const version = session
      await saveFinancialImports(owner.value, batches, original)
      if (version !== session || !canAccess.value) return
      const saved = new Map(batches.map((batch) => [batch.id, batch]))
      imports.value = [
        ...batches.filter((batch) => !imports.value.some((existing) => existing.id === batch.id)),
        ...imports.value.map((existing) => saved.get(existing.id) ?? existing),
      ]
      if (active.value && saved.has(active.value.id)) active.value = saved.get(active.value.id)!
    }
    const duplicateRows = (batch: FinancialImport): number => {
      const index = financialShipmentIndex(imports.value.filter((other) => other.carrier === batch.carrier))
      return batch.charges.filter((row) =>
        financialShipmentEntries(index, batch, row).some(
          (entry) =>
            entry.row.description === row.description &&
            entry.row.date === row.date &&
            entry.amount !== null &&
            row.amount !== null &&
            financialAmountsEqual(entry.amount, row.amount),
        ),
      ).length
    }
    const selectFiles = async (files: FileList | File[] | null): Promise<void> => {
      if (!canAccess.value || busy.value) return
      dragging.value = false
      if (loading.value) {
        error.value = 'Your workspace is still opening. Choose the file again in a moment.'
        return
      }
      error.value = ''
      message.value = ''
      const selected = Array.from(files ?? [])
      if (selected.length !== 1) {
        error.value = 'Choose one invoice file at a time.'
        return
      }
      const version = session
      const file = selected[0]
      busy.value = true
      try {
        // The carrier is recognised from the file's columns; unsupported reports are refused before saving.
        const parsed = await readFinancialFile(file, undefined, true)
        const carrier = financialImportCarrier(parsed)
        const hash = await financialFileHash(file)
        if (version !== session) return
        const duplicate = imports.value.find((batch) => batch.fileHash === hash)
        if (duplicate) {
          openImport(duplicate)
          message.value = 'This file is already imported. Your existing draft has been opened.'
          await focusInvoiceTitle()
          return
        }
        const batch = normalizeFinancialImport(parsed, carrier, file.name, hash, owner.value, userName.value)
        const sameInvoice = imports.value.find(
          (saved) => saved.carrier === batch.carrier && financialSameInvoiceNumber(saved.invoiceNumber, batch.invoiceNumber),
        )
        if (sameInvoice) {
          openImport(sameInvoice)
          message.value = `${batch.carrier} invoice ${batch.invoiceNumber} is already imported as ${sameInvoice.fileName}. That draft is open; delete it first if the new file should replace it.`
          await focusInvoiceTitle()
          return
        }
        const repeated = duplicateRows(batch)
        await persist([batch], file)
        if (version !== session) return
        openImport(batch)
        message.value = repeated
          ? `Imported ${numberLabel(batch.charges.length)} ${batch.carrier} rows. ${repeated} rows also appear on an earlier invoice with the same product, date and amount; check them under “Possible double billing”.`
          : `Imported ${numberLabel(batch.charges.length)} ${batch.carrier} rows. Match shipments to find the customers.`
        await focusInvoiceTitle()
      } catch (reason) {
        if (version === session) reportError(reason)
      } finally {
        if (version === session) {
          busy.value = false
          if (fileInput.value) fileInput.value.value = ''
        }
      }
    }
    const onFileChange = (event: Event): Promise<void> => {
      return selectFiles((event.target as HTMLInputElement).files)
    }
    const onDrop = (event: DragEvent): Promise<void> => {
      return selectFiles(event.dataTransfer?.files ?? null)
    }
    const onDragOver = (): void => {
      dragging.value = !busy.value && !loading.value
    }
    // The panel holds the select and the button, so moving over them is not leaving the drop target.
    const onDragLeave = (event: DragEvent): void => {
      if (!(event.currentTarget as HTMLElement).contains(event.relatedTarget as Node | null)) dragging.value = false
    }
    const matchImport = async (): Promise<void> => {
      if (!active.value || busy.value || !(await gridSettled())) return
      const version = session
      const batch = active.value
      const request = new AbortController()
      matchController = request
      busy.value = true
      matching.value = true
      error.value = ''
      message.value = ''
      progress.value = 'Starting lookup…'
      try {
        const result = await matchFinancialImport(
          batch,
          request.signal,
          (completed, total) => {
            progress.value = `${numberLabel(completed)} of ${numberLabel(total)} shipment numbers checked`
          },
          imports.value,
          financialKnownSplit(shipments.value, imports.value),
        )
        if (version !== session || request.signal.aborted) return
        await persist([result.batch])
        await settleSplit()
        void refreshMargins()
        const matched = result.batch.charges.filter((row) => row.matchStatus === 'matched').length
        message.value = [
          `Matching complete: ${numberLabel(matched)} of ${numberLabel(result.batch.charges.length)} rows are linked to a customer.`,
          result.linked ? `${result.linked} later rows were linked through earlier invoices.` : '',
          result.retained ? `${result.retained} rows kept their earlier match because PacSort no longer returns the shipment.` : '',
          result.reopened ? `${result.reopened} reviewed rows were reopened because PacSort data changed; their amounts and notes are kept.` : '',
          splitCustomers(result.batch) ? `${splitCustomers(result.batch)} customers ship from several sender addresses and are billed per address.` : '',
          result.addressesFailed ? 'Sender addresses could not be loaded for every customer; those rows are grouped per company. Refresh matches to retry.' : '',
        ]
          .filter(Boolean)
          .join(' ')
      } catch (reason) {
        if (version === session) {
          if (request.signal.aborted) message.value = 'Matching cancelled. Your saved draft is unchanged.'
          else reportError(reason)
        }
      } finally {
        if (version === session) {
          busy.value = false
          matching.value = false
          matchController = null
          // A finished match removes the callout with its button: focus moves to the costs instead of the page.
          await nextTick()
          const focused = document.activeElement
          if (!focused || focused === document.body || !focused.isConnected)
            document.getElementById('financial-results-title')?.focus()
        }
      }
    }
    const cancelMatching = (): void => {
      matchController?.abort()
    }
    // A company billed per sender address stays split everywhere once any shipment or row shows a second address.
    const settleSplit = async (): Promise<void> => {
      const version = session
      const split = financialKnownSplit(shipments.value, imports.value)
      if (!split.size) return
      const changed = financialApplySplit(shipments.value, imports.value, split)
      if (changed.imports.length) await persist(changed.imports)
      if (version !== session || !changed.shipments.length) return
      await saveFinancialShipments(owner.value, changed.shipments)
      if (version === session) shipments.value = financialMergeShipments(shipments.value, changed.shipments)
    }
    const saveMetadata = async (): Promise<void> => {
      if (!active.value || busy.value || !(await gridSettled())) return
      const batch = active.value
      error.value = ''
      message.value = ''
      metadataError.value = ''
      if (!invoiceNumber.value.trim() || !currency.value || (batch.carrier === 'PostNord' && !amountBasis.value)) {
        metadataError.value = `Complete the invoice number, currency${batch.carrier === 'PostNord' ? ' and amount basis' : ''}.`
        return
      }
      const total = invoiceTotal.value.trim() ? financialDecimal(invoiceTotal.value) : ''
      if (total === null) {
        metadataError.value = 'Enter the invoice total as a number, for example 93150.25, or leave it empty.'
        return
      }
      if (
        imports.value.some(
          (other) =>
            other.id !== batch.id &&
            other.carrier === batch.carrier &&
            financialSameInvoiceNumber(other.invoiceNumber, invoiceNumber.value),
        )
      ) {
        metadataError.value = `${batch.carrier} invoice ${invoiceNumber.value.trim()} already exists in your workspace. Open that import, or delete it first.`
        return
      }
      const changes = [
        batch.currency && batch.currency !== currency.value ? `currency ${batch.currency} → ${currency.value}` : '',
        batch.amountBasis && batch.amountBasis !== amountBasis.value ? 'amount basis' : '',
      ].filter(Boolean)
      let reopened = 0
      let cleared = 0
      // A price set in the review grid was in the old currency and amount basis: it no longer applies.
      const pricesLapse = batch.currency !== currency.value || batch.amountBasis !== amountBasis.value
      busy.value = true
      try {
        await persist([
          {
            ...batch,
            invoiceNumber: invoiceNumber.value.trim(),
            currency: currency.value,
            amountBasis: amountBasis.value,
            invoiceTotal: total,
            charges: batch.charges.map((row) => {
              let next = row
              if (pricesLapse && row.adjustment?.customerPrice != null) {
                cleared += 1
                next = { ...row, adjustment: { ...row.adjustment, customerPrice: null } }
                if (!next.adjustment!.exclude && !next.adjustment!.note.trim()) next = withoutAdjustment(next)
              }
              if (!changes.length || next.review?.status !== 'approved') return next
              reopened += 1
              return { ...next, review: financialReopenReview(next.review, `Invoice details changed: ${changes.join(', ')}.`) }
            }),
          },
        ])
        message.value = [
          'Invoice details saved.',
          reopened ? `${reopened} approved rows were reopened because the ${changes.join(' and ')} changed; their amounts are kept.` : '',
          cleared ? `${cleared} prices set in the review grid were cleared because the currency or amount basis changed.` : '',
        ]
          .filter(Boolean)
          .join(' ')
        if (selectedId.value) await closeInvoiceDetails()
      } catch (reason) {
        reportError(reason)
      } finally {
        busy.value = false
      }
    }
    const showCustomers = (): void => {
      view.value = 'customers'
      customerId.value = 'all'
      selectedId.value = ''
    }
    const showCharges = (): void => {
      view.value = 'charges'
      customerId.value = 'all'
      selectedId.value = ''
    }
    // "Show charges" is replaced by the customer's charges, so focus moves to the list's heading.
    const openCustomer = async (id: string): Promise<void> => {
      view.value = 'charges'
      customerId.value = id
      filter.value = 'all'
      search.value = ''
      selectedId.value = ''
      await nextTick()
      document.getElementById('financial-results-title')?.focus()
    }
    const previousPage = (): void => {
      page.value = Math.max(1, page.value - 1)
    }
    const nextPage = (): void => {
      page.value = Math.min(pageCount.value, page.value + 1)
    }
    const toggleRow = (id: string): void => {
      selection.value = selectionSet.value.has(id) ? selection.value.filter((value) => value !== id) : [...selection.value, id]
    }
    const togglePage = (): void => {
      const ids = pageRows.value.map((row) => row.id)
      selection.value = allOnPageSelected.value
        ? selection.value.filter((id) => !ids.includes(id))
        : [...new Set([...selection.value, ...ids])]
    }
    const selectAllFiltered = (): void => {
      selection.value = filteredRows.value.map((row) => row.id)
    }
    // The selection bar closes with its selection: focus moves to the page's select-all box, or the list's heading.
    const focusSelection = async (): Promise<void> => {
      await nextTick()
      const box = document.getElementById('financial-select-page') as HTMLInputElement | null
      if (box && !box.disabled) box.focus()
      else document.getElementById('financial-results-title')?.focus()
    }
    const clearSelection = async (): Promise<void> => {
      selection.value = []
      await focusSelection()
    }
    const focusTitle = async (): Promise<void> => {
      await nextTick()
      document.getElementById('financial-review-title')?.focus()
      document.getElementById('financial-review-title')?.scrollIntoView({ block: 'start' })
    }
    const selectCharge = async (id: string, event?: MouseEvent): Promise<void> => {
      if (event) reviewTrigger = event.currentTarget as HTMLElement
      selectedId.value = id
      invoiceDetailsVisible.value = false
      invoiceDetailsTrigger = null
      message.value = ''
      error.value = ''
      await focusTitle()
    }
    const closeReview = async (): Promise<void> => {
      const closed = selectedId.value
      selectedId.value = ''
      invoiceDetailsVisible.value = false
      invoiceDetailsTrigger = null
      message.value = ''
      error.value = ''
      const target = returnTarget.value
      const saved = invoiceTab
      const label = returnFocus
      returnTarget.value = ''
      invoiceTab = null
      returnFocus = ''
      if (target === 'shipment' && returnToShipment) {
        const itemId = returnToShipment
        returnToShipment = ''
        // Back to the shipment over the tab it was opened from, so its own back button returns to a review too.
        tab.value = returnTab
        if (saved) await restoreInvoiceTab(saved)
        await openShipment(itemId)
        // Focus returns to the row's Open row button, or stays on the shipment's heading.
        const button = [...document.querySelectorAll<HTMLElement>('.financial__shipment-rows [aria-label]')].find(
          (element) => element.getAttribute('aria-label') === label,
        )
        if (label && button) button.focus()
        return
      }
      if (target === 'ledger') {
        tab.value = 'shipments'
        if (saved) await restoreInvoiceTab(saved)
        await focusLedger(label)
        return
      }
      await nextTick()
      const index = filteredRows.value.findIndex((row) => row.id === closed)
      if (index >= 0) page.value = Math.floor(index / FINANCIAL_PAGE_SIZE) + 1
      await nextTick()
      if (reviewTrigger?.isConnected) reviewTrigger.focus()
      else document.getElementById('financial-results-title')?.focus()
    }
    // The details take the charge's place. An error left from the charge is cleared, so the form only shows its own.
    const openInvoiceDetails = (): Promise<void> => {
      const focused = document.activeElement
      invoiceDetailsTrigger = focused instanceof HTMLElement && focused !== document.body ? focused : null
      error.value = ''
      invoiceDetailsVisible.value = true
      return nextTick().then(() => {
        document.getElementById('financial-invoice')?.focus()
        document.getElementById('financial-invoice')?.scrollIntoView({ block: 'center' })
      })
    }
    // Back to the charge, focus returns to the control that opened the details. Once the details are saved complete,
    // that button is gone and the charge's heading takes the focus.
    const closeInvoiceDetails = async (): Promise<void> => {
      const trigger = invoiceDetailsTrigger
      invoiceDetailsTrigger = null
      invoiceDetailsVisible.value = false
      await nextTick()
      if (trigger?.isConnected && !(trigger as HTMLButtonElement).disabled) trigger.focus()
      else await focusTitle()
    }
    // The same names as the charge's own summary and the shipment detail.
    const statusLabel = (row: FinancialCharge): string => {
      if (row.review?.status === 'excluded') return 'Excluded'
      if (active.value && financialReady(active.value, row)) return signalsOf(row).billed ? 'Billed' : 'Approved'
      if (row.matchStatus === 'missing-id') return 'No shipment number'
      if (row.matchStatus === 'ambiguous') return 'Multiple matches'
      if (!row.match) return active.value?.matchedAt ? 'No match' : 'Not matched yet'
      return row.review?.reason ? 'Reopened' : 'To approve'
    }
    const rowHint = (row: FinancialCharge): string => {
      if (drafts.value[draftKey(row.id)]) return 'Unsaved draft'
      const signals = signalsOf(row)
      if (signals.warnings.some((warning) => warning.blocking)) return 'Check shipment rows'
      if (signals.service.state === 'different') return 'Service differs'
      return ''
    }
    const weightNote = (row: FinancialCharge): string => {
      const weight = signalsOf(row).weight
      if (weight.state === 'different') return weight.difference
      if (weight.state === 'same') return 'as booked'
      return weight.pacsort === 'Not available' ? '' : `booked ${weight.pacsort}`
    }
    // Removes a row's grid change, saved or still pending.
    const dropAdjustment = (batchId: string, row: FinancialCharge): FinancialCharge => {
      const next: FinancialCharge = { ...row }
      delete next.adjustment
      const key = `${batchId}:${row.id}`
      if (pendingAdjustments.value.has(key)) {
        const pending = new Map(pendingAdjustments.value)
        pending.delete(key)
        pendingAdjustments.value = pending
      }
      return next
    }
    // A decision made outside the review grid replaces the grid's change for that row.
    const withoutAdjustment = (row: FinancialCharge): FinancialCharge => {
      const next: FinancialCharge = { ...row }
      delete next.adjustment
      if (active.value && pendingAdjustments.value.has(`${active.value.id}:${row.id}`)) {
        const pending = new Map(pendingAdjustments.value)
        pending.delete(`${active.value.id}:${row.id}`)
        pendingAdjustments.value = pending
      }
      return next
    }
    const historyEvent = (review: FinancialReview): FinancialReviewEvent => ({
      status: review.status,
      customerPrice: review.customerPrice,
      purchasePrice: review.purchasePrice,
      note: review.note,
      userName: review.userName,
      reviewedAt: review.reviewedAt,
      reason: review.reason ?? '',
    })
    const reviewFor = (
      previous: FinancialReview | null,
      status: FinancialReviewStatus,
      values: {
        customerPrice: string | null
        purchasePrice: string | null
        note: string
        warningsChecked: boolean
        priceSource?: FinancialPriceSource
        marginPercent?: number | null
      },
    ): FinancialReview => ({
      status,
      customerPrice: values.customerPrice,
      purchasePrice: values.purchasePrice,
      note: values.note,
      userId: owner.value,
      userName: userName.value,
      reviewedAt: new Date().toISOString(),
      reason: status === 'pending' ? 'Reopened manually.' : undefined,
      warningsChecked: values.warningsChecked,
      priceSource: status === 'approved' ? (values.priceSource ?? 'manual') : undefined,
      marginPercent: status === 'approved' ? (values.marginPercent ?? null) : undefined,
      history: previous ? [...(previous.history ?? []), historyEvent(previous)] : [],
    })
    const saveReview = async (submission: FinancialReviewSubmission): Promise<void> => {
      if (!active.value || !selectedRow.value || busy.value || !(await gridSettled())) return
      const batch = active.value
      const id = selectedId.value
      const next = submission.next ? nextReviewId.value : ''
      busy.value = true
      error.value = ''
      message.value = ''
      try {
        await persist([
          {
            ...batch,
            charges: batch.charges.map((row) =>
              row.id === id
                ? {
                    ...withoutAdjustment(row),
                    review: reviewFor(row.review, submission.status, {
                      customerPrice: submission.customerPrice || null,
                      purchasePrice: submission.purchasePrice || null,
                      note: submission.note,
                      warningsChecked: submission.warningsChecked,
                      priceSource: submission.priceSource,
                      marginPercent: submission.marginPercent,
                    }),
                  }
                : row,
            ),
          },
        ])
        drafts.value = withoutDraft(draftKey(id))
        const done =
          submission.status === 'approved'
            ? 'Charge approved for the billing basis.'
            : submission.status === 'excluded'
              ? 'Charge excluded. Its cost stays visible as cost without revenue.'
              : 'Charge reopened for review. The earlier decision is kept in its history.'
        if (submission.next && next) {
          await selectCharge(next)
          message.value = `${done} Showing the next row that needs review.`
        } else message.value = submission.next ? `${done} No more rows need review in this list.` : done
      } catch (reason) {
        reportError(reason)
      } finally {
        busy.value = false
      }
    }
    // A selection of invoice rows can only be excluded, with one reason; rows are approved in the customer's review.
    const bulkExclude = async (payload: { note: string }): Promise<void> => {
      const rows = selectedRows.value
      if (!active.value || busy.value || !rows.length || !(await gridSettled())) return
      const batch = active.value
      const ids = new Set(rows.map((row) => row.id))
      busy.value = true
      error.value = ''
      message.value = ''
      try {
        await persist([
          {
            ...batch,
            charges: batch.charges.map((row) =>
              ids.has(row.id)
                ? {
                    ...withoutAdjustment(row),
                    review: reviewFor(row.review, 'excluded', {
                      customerPrice: null,
                      purchasePrice: null,
                      note: payload.note,
                      warningsChecked: false,
                    }),
                  }
                : row,
            ),
          },
        ])
        selection.value = []
        drafts.value = Object.fromEntries(
          Object.entries(drafts.value).filter(([key]) => !(key.startsWith(`${batch.id}:`) && ids.has(key.slice(batch.id.length + 1)))),
        )
        message.value = `${rows.length} rows excluded. Their cost stays visible as cost without revenue.`
        await focusSelection()
      } catch (reason) {
        reportError(reason)
      } finally {
        busy.value = false
      }
    }
    const splitCustomers = (batch: FinancialImport): number =>
      new Set(
        batch.charges
          .filter((row) => row.match && financialBillingId(row.match) !== row.match.companyId)
          .map((row) => row.match!.companyId),
      ).size
    const saveDraft = (draft: FinancialReviewDraft | null): void => {
      if (!selectedRow.value) return
      const key = draftKey(selectedRow.value.id)
      drafts.value = draft ? { ...drafts.value, [key]: draft } : withoutDraft(key)
    }
    const confirmMapping = async (product: string, service: string): Promise<void> => {
      if (!active.value || !product || !service) return
      const mapping: FinancialServiceMapping = {
        id: financialServiceMappingId(active.value.carrier, product),
        carrier: active.value.carrier,
        product,
        service,
        confirmedBy: userName.value,
        confirmedAt: new Date().toISOString(),
      }
      try {
        await saveFinancialMapping(owner.value, mapping)
        mappings.value = [...mappings.value.filter((item) => item.id !== mapping.id), mapping]
        message.value = `Confirmed: ${mapping.carrier} “${product}” = ${service}. Rows with this product are now checked against it.`
      } catch (reason) {
        reportError(reason)
      }
    }
    const openEntry = async (importId: string, rowId: string): Promise<void> => {
      const target = imports.value.find((batch) => batch.id === importId)
      if (!target) return
      if (target.id !== active.value?.id) {
        openImport(target)
        view.value = 'charges'
        // Cleared filters reset the list's page and open row once Vue flushes; the charge opens after that.
        await nextTick()
      }
      await selectCharge(rowId)
    }
    const showTab = (name: 'shipments' | 'invoices' | 'customers'): void => {
      tab.value = name
      selectedShipmentId.value = ''
      selectedGroup.value = null
      returnToShipment = ''
      returnTarget.value = ''
      invoiceTab = null
      message.value = ''
      error.value = ''
      billingNotice.value = ''
      billingFailure.value = ''
    }
    // A new period or "Only rows not billed yet" in the Bill section: the result of the last export no longer applies.
    const rememberBilling = (selection: FinancialBillingSelection): void => {
      billingPeriod.value = { from: selection.from, to: selection.to, onlyNew: selection.onlyNew }
      billingNotice.value = ''
      billingFailure.value = ''
    }
    // The customer list's "Show in Shipments": the period's shipments and the costs without a known shipment.
    const goShipments = async (): Promise<void> => {
      showTab('shipments')
      await nextTick()
      document.getElementById('financial-ledger-title')?.focus()
    }
    // The empty states' "Import a carrier invoice": the file input, or the open invoice's heading when one is open.
    const goInvoices = async (): Promise<void> => {
      showTab('invoices')
      await nextTick()
      const file = document.getElementById('financial-file') as HTMLInputElement | null
      if (file && !file.disabled) file.focus()
      else (document.getElementById('financial-invoice-title') ?? document.getElementById('financial-imports-title'))?.focus()
    }
    // Arrow keys move between tabs (WAI-ARIA tabs pattern).
    const onTabKey = (event: KeyboardEvent): void => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
      const ids = tabs.value.map((item) => item.id)
      const index = ids.indexOf(tab.value)
      const next =
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? ids.length - 1
            : (index + (event.key === 'ArrowRight' ? 1 : -1) + ids.length) % ids.length
      event.preventDefault()
      showTab(ids[next])
      nextTick(() => document.getElementById(`financial-tab-${ids[next]}`)?.focus())
    }
    // Returns focus to the ledger button that opened a detail, or to the ledger heading when it is off the page.
    const focusLedger = async (label: string): Promise<void> => {
      await nextTick()
      const button = [...document.querySelectorAll<HTMLElement>('#financial-panel-shipments [aria-label]')].find(
        (element) => element.getAttribute('aria-label') === label,
      )
      if (label && button) button.focus()
      else document.getElementById('financial-ledger-title')?.focus()
    }
    // The shipment number in the list is its open button, labelled with every number the shipment has.
    const focusShipmentButton = (itemId: string): Promise<void> => {
      const numbers = ledger.value.find((entry) => entry.shipment.itemId === itemId)?.shipment.trackingNumbers.join(', ')
      return focusLedger(numbers ? `Open shipment ${numbers}` : '')
    }
    const openShipment = async (itemId: string, keepMessage = false): Promise<void> => {
      selectedShipmentId.value = itemId
      if (!keepMessage) {
        message.value = ''
        error.value = ''
      }
      await nextTick()
      document.getElementById('financial-shipment-title')?.focus()
      document.getElementById('financial-shipment-title')?.scrollIntoView({ block: 'start' })
    }
    const closeShipment = async (): Promise<void> => {
      const itemId = selectedShipmentId.value
      selectedShipmentId.value = ''
      message.value = ''
      error.value = ''
      // Back in a review, the grid keeps its place and gets the focus back. A review that closed meanwhile leaves the
      // customer list.
      if (tab.value === 'customers') {
        await nextTick()
        const target = selectedGroup.value ? '#financial-panel-customers .financial__grid' : '#financial-overview-title'
        document.querySelector<HTMLElement>(target)?.focus()
        return
      }
      await focusShipmentButton(itemId)
    }
    const openLineFromShipment = async (importId: string, rowId: string): Promise<void> => {
      const line = selectedShipment.value?.lines.find((item) => item.batch.id === importId && item.row.id === rowId)
      returnToShipment = selectedShipmentId.value
      returnTab = tab.value === 'customers' ? 'customers' : 'shipments'
      returnTarget.value = 'shipment'
      // The shipment detail's Open row button for this row.
      const invoice = line ? `${line.batch.carrier} invoice ${financialInvoiceLabel(line.batch)}` : ''
      returnFocus = line ? `Open ${line.row.description}, row ${line.row.rowNumber} on ${invoice}` : ''
      rememberInvoiceTab()
      selectedShipmentId.value = ''
      tab.value = 'invoices'
      await openEntry(importId, rowId)
    }
    const openOrphan = async (importId: string, rowId: string): Promise<void> => {
      const batch = imports.value.find((item) => item.id === importId)
      const row = batch?.charges.find((item) => item.id === rowId)
      returnToShipment = ''
      returnTarget.value = 'ledger'
      returnFocus = batch && row ? `Open row ${row.rowNumber} on ${batch.carrier} invoice ${financialInvoiceLabel(batch)}` : ''
      rememberInvoiceTab()
      tab.value = 'invoices'
      await openEntry(importId, rowId)
    }
    const isOpenLine = (line: FinancialShipmentEntry): boolean =>
      !financialReady(line.batch, line.row) && line.row.review?.status !== 'excluded'
    const lineBlocker = (line: FinancialShipmentEntry): string | null => {
      if (line.row.matchStatus !== 'matched' || !line.row.match) return 'Not linked to the shipment'
      return bulkBlockerOf(line.batch, line.row, financialRowSignals(line.batch, line.row, shipmentIndex.value, mappings.value))
    }
    // A shipment has one delivery: with several delivery rows, nothing is approved in bulk until one is resolved.
    const blockerFor = (entry: FinancialLedgerEntry | undefined, line: FinancialShipmentEntry): string | null => {
      if (entry && line.role === 'base' && entry.deliveries > 1) return 'Several delivery rows for this shipment'
      return lineBlocker(line)
    }
    const detailBlocker: ComputedRef<(line: FinancialShipmentEntry) => string | null> = computed(() => {
      const entry = selectedShipment.value
      return (line) => blockerFor(entry, line)
    })
    const lineKey = (line: FinancialLine): string => `${line.batch.id}:${line.row.id}`
    // Checks a reviewer's grid change cannot settle: the row must first be valid, linked and still open.
    const STRUCTURAL = new Set([
      'Invoice details are missing',
      'No unique shipment',
      'Not linked to the shipment',
      'No known shipment',
      'Invalid invoice amount',
      'Already billed',
      'Changed after billing',
      'Already approved',
      'Excluded',
      'Booked size not loaded; refresh matches',
      'Booked price is in SEK',
    ])
    // A row's approval state. A change in the review grid (price, exclusion or note) is the reviewer's decision, so
    // it settles checks such as a loss or a possible double billing; structural checks still apply.
    const decide = (line: FinancialLine, blocker: string | null): FinancialLineState => {
      const open = !financialReady(line.batch, line.row) && line.row.review?.status !== 'excluded'
      const adjustment = line.row.adjustment
      if (!open) return { open, edited: false, exclude: false, eligible: false, reason: null, price: null }
      if (adjustment?.exclude) {
        const reason = adjustment.note.trim() ? null : 'Add a reason to exclude'
        return { open, edited: true, exclude: true, eligible: !reason, reason, price: null }
      }
      const price = suggestionOf(line.batch, line.row)
      const unpriced = price ? null : financialUnpricedReason(line.batch, line.row, margins.value)
      // An edit settles decision checks, never structural ones, whatever order the checks run in.
      const reason = adjustment
        ? (blocker && STRUCTURAL.has(blocker) ? blocker : null) ?? structuralBlocker(line) ?? unpriced
        : (blocker ?? unpriced)
      return { open, edited: !!adjustment, exclude: false, eligible: !reason, reason, price }
    }
    // The structural checks of a row, independent of the decision checks that may run before them.
    const structuralBlocker = (line: FinancialLine): string | null => {
      const { batch, row } = line
      if (!financialMetadataComplete(batch)) return 'Invoice details are missing'
      if (row.matchStatus !== 'matched' || !row.match) return 'Not linked to the shipment'
      if (financialChargeAmount(batch, row) === null) return 'Invalid invoice amount'
      const billing = financialBillingState(batch, row.id)
      if (billing === 'changed') return 'Changed after billing'
      if (billing === 'billed') return 'Already billed'
      if (financialSizeUnknown(batch, row)) return 'Booked size not loaded; refresh matches'
      if (financialRole(batch, row) === 'base' && row.match.bookedPrice != null && batch.currency && batch.currency !== 'SEK')
        return 'Booked price is in SEK'
      return null
    }
    // Every invoice row's approval state, computed once: open or not, approvable at its PacSort price, or why not.
    const lineStates: ComputedRef<Map<string, FinancialLineState>> = computed(() => {
      const states = new Map<string, FinancialLineState>()
      for (const entry of ledger.value)
        for (const line of entry.lines) {
          states.set(lineKey(line), decide(line, line.amount === null ? 'Invalid invoice amount' : blockerFor(entry, line)))
        }
      return states
    })
    const stateOf = (line: FinancialLine): FinancialLineState =>
      lineStates.value.get(lineKey(line)) ?? decide(line, 'No known shipment')
    const eligibleLines = (entry: FinancialLedgerEntry): FinancialShipmentEntry[] =>
      entry.lines.filter((line) => stateOf(line).eligible)
    const eligibleCount = (entry: FinancialLedgerEntry): number => eligibleLines(entry).length
    // What keeps the open rows of a shipment from being approved together, shown in the ledger.
    const entryReasons = (entry: FinancialLedgerEntry): string[] => [
      ...new Set(entry.lines.map((line) => stateOf(line).reason).filter((reason): reason is string => !!reason)),
    ]
    const lineSuggestion = (line: FinancialLine): FinancialPriceSuggestion | null => suggestionOf(line.batch, line.row)
    // Whether the shipment was opened from this customer's own page, which is still open under it.
    const fromGroup = (billingId: string): boolean => tab.value === 'customers' && selectedGroup.value?.id === billingId
    // A shipment's "Review {customer}": the customer's page, where rows are checked, edited, approved and billed. From
    // that page itself it is the way back, which keeps the review's undo history and the grid's place.
    const reviewCustomer = (billingId: string): Promise<void> => {
      if (fromGroup(billingId)) return closeShipment()
      showTab('customers')
      return openGroup(billingId)
    }
    // A shipment's "Bill {customer}": the same page, at its Bill section.
    const billCustomer = async (billingId: string): Promise<void> => {
      if (!fromGroup(billingId)) {
        showTab('customers')
        return openGroup(billingId, 'financial-billing-title')
      }
      selectedShipmentId.value = ''
      message.value = ''
      error.value = ''
      await nextTick()
      document.getElementById('financial-billing-title')?.focus()
      document.getElementById('financial-billing-title')?.scrollIntoView({ block: 'start' })
    }
    const refreshMargins = async (): Promise<void> => {
      const version = session
      const companyIds = [
        ...imports.value.flatMap((batch) => batch.charges.map((row) => row.match?.companyId ?? '')),
        ...shipments.value.map((record) => record.companyId),
      ]
      // Nothing missing: a notice left from an earlier failure no longer applies.
      if (!companyIds.some((id) => id && !margins.value[id])) {
        marginNotice.value = ''
        return
      }
      marginController?.abort()
      const request = new AbortController()
      marginController = request
      marginLoading.value = true
      try {
        const result = await loadFinancialMargins(companyIds, margins.value, request.signal)
        if (version !== session || request.signal.aborted) return
        margins.value = result.margins
        marginNotice.value = result.failed
          ? `Customer margins could not be loaded for ${result.failed} ${result.failed === 1 ? 'customer' : 'customers'}. Their fees and credits need a price entered by hand.`
          : ''
      } catch {
        // Aborted by a newer request or by leaving the page.
      } finally {
        if (marginController === request) marginLoading.value = false
      }
    }
    // The notice's Retry. Once every margin has loaded, the notice and its button are gone, so focus moves on to the
    // open tab.
    const retryMargins = async (): Promise<void> => {
      if (marginLoading.value) return
      const version = session
      await refreshMargins()
      if (version !== session || marginNotice.value) return
      message.value = 'Customer margins loaded.'
      await nextTick()
      if (document.activeElement === document.body) document.getElementById(`financial-tab-${tab.value}`)?.focus()
    }
    const loadShipments = async (request: { from: string; to: string; carriers: FinancialActiveCarrier[] }): Promise<void> => {
      // Not while the workspace opens: its saved shipments would replace what this load adds.
      if (loading.value || ledgerLoading.value || busy.value) return
      const version = session
      const abort = new AbortController()
      ledgerController = abort
      ledgerLoading.value = true
      error.value = ''
      ledgerResult.value = ''
      ledgerProgress.value = 'Asking PacSort for shipments…'
      try {
        const result = await loadFinancialShipments(
          request.from,
          request.to,
          request.carriers,
          abort.signal,
          (received, total) => {
            ledgerProgress.value = `${numberLabel(received)} of ${numberLabel(total)} shipments loaded`
          },
          financialKnownSplit(shipments.value, imports.value),
        )
        if (version !== session || abort.signal.aborted) return
        // Saved merged, so a shipment loaded once per carrier keeps every carrier's numbers after a reload.
        const merged = financialMergeShipments(shipments.value, result.records)
        const loaded = new Set(result.records.map((record) => record.itemId))
        await saveFinancialShipments(owner.value, merged.filter((record) => loaded.has(record.itemId)))
        if (version !== session) return
        shipments.value = merged
        await settleSplit()
        void refreshMargins()
        rememberPeriod({
          from: request.from,
          to: request.to,
          carriers: [...request.carriers],
          at: new Date().toISOString(),
          count: result.records.length,
        })
        const withCost = ledger.value.filter((entry) => loaded.has(entry.shipment.itemId) && entry.lines.length).length
        ledgerResult.value = [
          `Loaded ${numberLabel(result.records.length)} shipments booked ${request.from} – ${request.to}.`,
          `${numberLabel(withCost)} already have carrier costs; ${numberLabel(result.records.length - withCost)} are waiting for an invoice.`,
          result.addressesFailed ? 'Sender addresses could not be loaded for every customer; those shipments are grouped per company.' : '',
        ]
          .filter(Boolean)
          .join(' ')
      } catch (reason) {
        if (version === session) {
          if (abort.signal.aborted) ledgerResult.value = 'Loading cancelled. Nothing was saved.'
          else reportError(reason)
        }
      } finally {
        if (version === session) {
          ledgerLoading.value = false
          ledgerProgress.value = ''
          ledgerController = null
        }
      }
    }
    const cancelLoad = (): void => {
      ledgerController?.abort()
    }
    // Approves every eligible row of a group at its price (PacSort's, or the reviewer's from the grid) and excludes the
    // rows the reviewer chose not to bill. Rows that still need a check stay open.
    const approveLines = async (candidates: FinancialLine[], scope: string): Promise<void> => {
      if (busy.value || !(await gridSettled())) return
      const fresh = new Set(candidates.map((line) => lineKey(line)))
      const current = imports.value.flatMap((batch) =>
        batch.charges.filter((row) => fresh.has(`${batch.id}:${row.id}`)).map((row) => ({ batch, row })),
      )
      const lines = current.filter((line) => stateOf(line).eligible)
      const left = current.filter((line) => stateOf(line).open && !stateOf(line).eligible).length
      if (!lines.length) return
      const decisions = new Map(lines.map((line) => [lineKey(line), stateOf(line)]))
      const rowsByBatch = new Map<string, Set<string>>()
      for (const line of lines) rowsByBatch.set(line.batch.id, new Set([...(rowsByBatch.get(line.batch.id) ?? []), line.row.id]))
      busy.value = true
      error.value = ''
      message.value = ''
      try {
        await persist(
          imports.value
            .filter((batch) => rowsByBatch.has(batch.id))
            .map((batch) => ({
              ...batch,
              charges: batch.charges.map((row) => {
                if (!rowsByBatch.get(batch.id)!.has(row.id)) return row
                const decision = decisions.get(`${batch.id}:${row.id}`)!
                const note = row.adjustment?.note.trim() ?? ''
                const rest: FinancialCharge = { ...row }
                delete rest.adjustment
                if (decision.exclude)
                  return {
                    ...rest,
                    review: reviewFor(row.review, 'excluded', {
                      customerPrice: null,
                      purchasePrice: null,
                      note,
                      warningsChecked: true,
                    }),
                  }
                const price = decision.price!
                return {
                  ...rest,
                  review: reviewFor(row.review, 'approved', {
                    customerPrice: price.amount,
                    purchasePrice: row.review?.purchasePrice ?? null,
                    note:
                      note ||
                      (price.source === 'manual'
                        ? `Approved at the price set in review by ${row.adjustment?.userName ?? userName.value} · ${scope}.`
                        : `Approved at the PacSort price (${price.label}) · ${scope}.`),
                    warningsChecked: !!row.adjustment,
                    priceSource: price.source,
                    marginPercent: price.marginPercent,
                  }),
                }
              }),
            })),
        )
        drafts.value = Object.fromEntries(Object.entries(drafts.value).filter(([key]) => !decisions.has(key)))
        undoStack.value = []
        redoStack.value = []
        const approved = lines.filter((line) => !decisions.get(lineKey(line))!.exclude)
        const excluded = lines.length - approved.length
        const shipmentCount = new Set(approved.map((line) => line.row.match?.itemId)).size
        const total = sumFinancialAmounts(approved.map((line) => decisions.get(lineKey(line))!.price!.amount))
        const currencies = [...new Set(approved.map((line) => line.batch.currency))]
        const manual = approved.some((line) => decisions.get(lineKey(line))!.price!.source === 'manual')
        message.value = [
          approved.length
            ? `${numberLabel(approved.length)} cost ${approved.length === 1 ? 'row' : 'rows'} on ${numberLabel(shipmentCount)} ${shipmentCount === 1 ? 'shipment' : 'shipments'} approved at ${manual ? 'the prices from the review' : 'PacSort prices'} (${financialAmountLabel(total, currencies.length === 1 ? currencies[0] : '')}) · ${scope}.`
            : '',
          excluded ? `${numberLabel(excluded)} ${excluded === 1 ? 'row was' : 'rows were'} excluded with your reason.` : '',
          left ? `${numberLabel(left)} ${left === 1 ? 'row needs' : 'rows need'} a check and stayed open.` : '',
          approved.length ? 'Bill them below.' : '',
        ]
          .filter(Boolean)
          .join(' ')
        // The shipment's summary only exists while something differs, so the shipment's title takes the focus.
        if (selectedShipment.value) {
          await nextTick()
          document.getElementById('financial-shipment-title')?.focus()
        }
      } catch (reason) {
        reportError(reason)
      } finally {
        busy.value = false
      }
    }
    // The approval overview: one row per billing customer.
    const overviewRow = (id: string, label: string, meta: string[], lines: FinancialLine[]): FinancialOverviewRow => {
      let ready = 0
      let check = 0
      let toBill = 0
      let billed = 0
      let excluded = 0
      let toExclude = 0
      const readyPrices: string[] = []
      const prices: string[] = []
      const pricedCosts: (string | null)[] = []
      const costs: (string | null)[] = []
      for (const line of lines) {
        const state = stateOf(line)
        const cost = financialChargeAmount(line.batch, line.row)
        costs.push(cost)
        if (line.row.review?.status === 'excluded') excluded += 1
        else if (state.eligible && state.exclude) toExclude += 1
        else if (state.eligible) {
          ready += 1
          readyPrices.push(state.price!.amount)
          prices.push(state.price!.amount)
          pricedCosts.push(cost)
        } else if (state.open) check += 1
        else {
          if (financialBillingState(line.batch, line.row.id) === 'billed') billed += 1
          else toBill += 1
          prices.push(line.row.review!.customerPrice!)
          pricedCosts.push(cost)
        }
      }
      const sum = (values: (string | null)[]): string | null =>
        values.some((value) => value === null) ? null : sumFinancialAmounts(values as string[])
      // Amounts are only added up within one currency; an unconfirmed currency next to a confirmed one is mixed too.
      const currencies = [...new Set(lines.map((line) => line.batch.currency))]
      const single = currencies.length <= 1
      const price = single ? sumFinancialAmounts(prices) : null
      const pricedCost = single ? sum(pricedCosts) : null
      return {
        id,
        label,
        meta,
        quotedPrice: financialQuotedTotal(lines),
        ready,
        readyAmount: single ? sumFinancialAmounts(readyPrices) : null,
        toExclude,
        currency: single ? (currencies[0] ?? '') : '',
        check,
        toBill,
        billed,
        excluded,
        cost: single ? sum(costs) : null,
        price,
        margin: price === null || pricedCost === null || !prices.length ? null : subtractFinancialAmounts(price, pricedCost),
        pricedCost,
      }
    }
    const periodEntries: ComputedRef<FinancialLedgerEntry[]> = computed(() => financialBookingEntries(ledger.value, bookingPeriod.value))
    // Older unsettled work stays reachable without adding it to the selected period's totals.
    const olderEntries: ComputedRef<FinancialLedgerEntry[]> = computed(() => {
      if (!bookingPeriod.value.from) return []
      return financialBookingEntries(ledger.value, { ...bookingPeriod.value, from: '', to: bookingPeriod.value.from })
        .filter((entry) => financialLocalDate(entry.shipment.bookedAt) < bookingPeriod.value.from)
        .filter((entry) => entry.open > 0 || entry.ready > 0 || entry.changed > 0 || financialQuoteCandidates(
          [entry], quotes.value, { customerId: entry.shipment.billingId, from: '', to: '', onlyNew: true },
        ).length > 0)
    })
    const customerEntries: ComputedRef<FinancialLedgerEntry[]> = computed(() => customerScope.value === 'invoices' ? ledger.value.filter((entry) => entry.lines.length > 0) : showOlder.value ? olderEntries.value : periodEntries.value)
    const customerScopeLabel: ComputedRef<string> = computed(() => customerScope.value === 'invoices'
      ? 'All customers linked to your imported invoice rows, across all carriers and booking dates. Rows without a known customer remain unassigned.'
      : showOlder.value
      ? `Older work · booked before ${bookingPeriod.value.from} · selected carriers only`
      : `Booked ${bookingPeriod.value.from} – ${bookingPeriod.value.to} · ${bookingPeriod.value.carriers.join(', ')} · same shipments as the Shipments view`)
    const customerEmptyText: ComputedRef<string> = computed(() => customerScope.value === 'invoices'
      ? 'No customers have been linked to your invoices yet. Open Carrier invoices and match shipments.'
      : showOlder.value
      ? 'No older work remains in the loaded shipments for these carriers.'
      : 'No customers in the shipments known for this booking period. Use Load from PacSort above to check the complete period.')
    const overviewCustomers: ComputedRef<FinancialOverviewRow[]> = computed(() => {
      const groups = new Map<string, { name: string; shipments: number; lines: FinancialLine[]; entries: FinancialLedgerEntry[] }>()
      for (const entry of customerEntries.value) {
        const group = groups.get(entry.shipment.billingId) ?? { name: entry.shipment.billingName, shipments: 0, lines: [], entries: [] }
        group.shipments += 1
        group.entries.push(entry)
        group.lines.push(...entry.lines)
        groups.set(entry.shipment.billingId, group)
      }
      // Where the customer's costs came from: the invoice file, or how many, and when the latest was imported.
      const sourceOf = (lines: FinancialLine[]): string => {
        const batches = [...new Map(lines.map((line) => [line.batch.id, line.batch])).values()]
        if (!batches.length) return 'Quoted bookings from PacSort · carrier invoice pending'
        const latest = batches.reduce((last, batch) => (batch.createdAt > last.createdAt ? batch : last))
        const imported = `imported ${dateLabel(latest.createdAt)} by ${latest.createdByName}`
        return batches.length === 1
          ? `From ${latest.carrier} ${financialInvoiceLabel(latest)} (${latest.fileName}) · ${imported}`
          : `From ${batches.length} invoice files · latest ${latest.carrier} ${financialInvoiceLabel(latest)}, ${imported}`
      }
      return [...groups]
        .map(([id, group]) => {
          const entries = group.entries
          const prices = entries.map((entry) => entry.shipment.bookedPrice)
          const pending = financialQuoteCandidates(entries, quotes.value, { customerId: id, from: '', to: '', onlyNew: true })
          const summary = overviewRow(id, group.name, [
            `${numberLabel(group.shipments)} ${group.shipments === 1 ? 'shipment' : 'shipments'}`, sourceOf(group.lines),
          ], group.lines)
          return {
            ...summary,
            quotedPrice: prices.some((price) => price === null) ? null : sumFinancialAmounts(prices as string[]),
            cost: group.lines.length ? summary.cost : null,
            quoteToBill: pending.length,
          }
        })
        .sort((a, b) => b.ready + b.check + b.quoteToBill - (a.ready + a.check + a.quoteToBill) || a.label.localeCompare(b.label))
    })
    // Review of one customer before approval: every row of the customer, from every invoice.
    const groupLines = (id: string): FinancialLine[] =>
      customerEntries.value.filter((entry) => entry.shipment.billingId === id).flatMap((entry) => entry.lines)
    const approveGroup = (id: string): Promise<void> => {
      const entries = customerEntries.value.filter((entry) => entry.shipment.billingId === id)
      return approveLines(groupLines(id), entries[0]?.shipment.billingName ?? 'customer')
    }
    const groupRow: ComputedRef<FinancialOverviewRow | undefined> = computed(() => {
      const group = selectedGroup.value
      return group ? overviewCustomers.value.find((row) => row.id === group.id) : undefined
    })
    // The open customer can disappear while its page is shown: a match or a load from PacSort that finds a second
    // sender address bills the company per address from then on, and a deleted import can take its costs. The page
    // then closes instead of going blank, and says so when the customer was split.
    watch(groupRow, async (row, previous) => {
      const group = selectedGroup.value
      if (!group || row) return
      const split = overviewCustomers.value.some((item) => item.id.startsWith(`${group.id}:`))
      const notice = split
        ? `${previous?.label ?? 'This customer'} is now billed per sender address. Each address is listed as its own customer.`
        : ''
      if (selectedShipment.value) {
        // The shipment stays open; its back button returns to the customer list.
        selectedGroup.value = null
        undoStack.value = []
        redoStack.value = []
        if (notice) message.value = notice
        return
      }
      await closeGroup()
      if (notice) message.value = notice
    })
    const selectedQuoteBilling: ComputedRef<FinancialQuoteBilling | null> = computed(() => quotes.value.find((quote) => quote.itemId === selectedShipment.value?.shipment.itemId) ?? null)
    const financialQuoteEligible: ComputedRef<boolean> = computed(() => !!selectedShipment.value && financialQuoteCandidates(
      [selectedShipment.value], quotes.value, { customerId: selectedShipment.value.shipment.billingId, from: '', to: '', onlyNew: true },
    ).length > 0)
    const groupData: ComputedRef<ReturnType<typeof financialGroupReview> | null> = computed(() => (selectedGroup.value ? financialGroupReview(groupLines(selectedGroup.value.id), stateOf) : null))
    const openGroup = async (id: string, focus = 'financial-group-title'): Promise<void> => {
      if (!overviewCustomers.value.some((row) => row.id === id)) {
        error.value = 'Load this customer’s booking period or match its invoice first.'
        return
      }
      // Undo belongs to the review it was made in.
      if (selectedGroup.value?.id !== id) {
        undoStack.value = []
        redoStack.value = []
      }
      selectedGroup.value = { id }
      message.value = ''
      error.value = ''
      billingNotice.value = ''
      billingFailure.value = ''
      await nextTick()
      document.getElementById(focus)?.focus()
      document.getElementById(focus)?.scrollIntoView({ block: 'start' })
    }
    const closeGroup = async (): Promise<void> => {
      const label = groupRow.value?.label ?? ''
      selectedGroup.value = null
      undoStack.value = []
      redoStack.value = []
      message.value = ''
      error.value = ''
      billingNotice.value = ''
      billingFailure.value = ''
      await nextTick()
      const button = [...document.querySelectorAll<HTMLElement>('#financial-panel-customers [aria-label]')].find(
        (element) => element.getAttribute('aria-label') === `Review ${label} before approving`,
      )
      // A customer with nothing left to bill is folded: open the fold so focus returns to its row.
      const fold = button?.closest('details')
      if (fold) fold.open = true
      if (button) button.focus()
      else document.getElementById('financial-overview-title')?.focus()
    }
    const approveSelectedGroup = (): Promise<void> =>
      selectedGroup.value ? approveGroup(selectedGroup.value.id) : Promise.resolve()

    // Review grid: edits are saved as they are made, like a spreadsheet, and can be undone.
    const adjustmentOf = (line: FinancialLine): FinancialAdjustment | null => {
      const key = lineKey(line)
      return pendingAdjustments.value.has(key) ? pendingAdjustments.value.get(key)! : (line.row.adjustment ?? null)
    }
    const basisLabel = (line: FinancialLine): string => {
      const review = line.row.review
      if (review?.status === 'approved') {
        if (review.priceSource === 'booked') return 'PacSort booked price'
        if (review.priceSource === 'margin') return `Cost + ${review.marginPercent ?? '?'}% margin`
        return review.priceSource === 'manual' ? 'Set by reviewer' : 'Approved amount'
      }
      if (review?.status === 'excluded') return 'Not billed'
      const adjustment = adjustmentOf(line)
      if (adjustment?.exclude) return 'Not billed'
      if (adjustment?.customerPrice != null) return 'Set in review'
      return financialPriceSuggestion(line.batch, line.row, margins.value)?.label ?? 'No PacSort price'
    }
    const gridRows: ComputedRef<FinancialGridRow[]> = computed(() => {
      const group = selectedGroup.value
      if (!group) return []
      return groupLines(group.id).map((line) => {
        const state = stateOf(line)
        const adjustment = adjustmentOf(line)
        const review = line.row.review
        const cost = financialChargeAmount(line.batch, line.row)
        const pacsort = financialPriceSuggestion(line.batch, line.row, margins.value)?.amount ?? null
        const excluded = review?.status === 'excluded'
        const price = financialReady(line.batch, line.row)
          ? review!.customerPrice!
          : excluded || adjustment?.exclude
            ? null
            : (adjustment?.customerPrice ?? pacsort)
        const margin = price !== null && cost !== null ? subtractFinancialAmounts(price, cost) : null
        const billing = financialBillingState(line.batch, line.row.id)
        let status = 'Ready'
        let tone: FinancialGridRow['tone'] = 'ready'
        if (!state.open) {
          status = excluded ? 'Excluded' : billing === 'billed' ? 'Billed' : billing === 'changed' ? 'Changed after billing' : 'Approved'
          // A row changed after billing is not open: it is billed again or credited in the Bill section, so it has its own
          // tone and never counts as a row that needs a check.
          tone = excluded ? 'excluded' : billing === 'changed' ? 'changed' : 'done'
        } else if (adjustment?.exclude) {
          status = adjustment.note.trim() ? 'Will be excluded' : 'Add a reason to exclude'
          tone = adjustment.note.trim() ? 'excluded' : 'check'
        } else if (state.reason) {
          status = 'Needs a check'
          tone = 'check'
        } else if (adjustment) status = 'Ready · edited'
        return {
          key: lineKey(line),
          itemId: line.row.match?.itemId ?? null,
          shipment: line.row.trackingNumber || 'No shipment number',
          status,
          reason: state.open && !adjustment?.exclude ? (state.reason ?? '') : '',
          tone,
          charge: line.row.description,
          role: financialRole(line.batch, line.row),
          cost,
          price,
          pacsortPrice: pacsort,
          quotedPrice: line.row.match?.bookedPrice ?? null,
          margin,
          marginPercent: margin !== null && cost !== null ? financialPercent(margin, cost) : null,
          basis: basisLabel(line),
          bill: !excluded && !adjustment?.exclude,
          note: state.open ? (adjustment?.note ?? '') : (review?.note ?? ''),
          booked: line.row.match ? financialLocalDate(line.row.match.createdAt) : '',
          service: line.row.match?.service ?? '',
          grams: line.row.match?.weightGrams ?? null,
          editable: state.open,
          edited: !!adjustment,
        }
      })
    })
    const sameAdjustment = (left: FinancialAdjustment | null, right: FinancialAdjustment | null): boolean =>
      (left?.customerPrice ?? null) === (right?.customerPrice ?? null) &&
      !!left?.exclude === !!right?.exclude &&
      (left?.note ?? '') === (right?.note ?? '')
    const applyAdjustments = (changes: [string, FinancialAdjustment | null][]): void => {
      pendingAdjustments.value = new Map([...pendingAdjustments.value, ...changes])
      gridSave.value = 'saving'
      window.clearTimeout(gridTimer)
      // While another action writes the invoice files, the grid waits its turn.
      const tick = (): void => {
        if (busy.value) gridTimer = window.setTimeout(tick, 250)
        else gridChain = gridChain.then(flushAdjustments)
      }
      gridTimer = window.setTimeout(tick, 250)
    }
    const editGrid = (edits: FinancialGridEdit[]): void => {
      const group = selectedGroup.value
      if (!group) return
      const lines = new Map(groupLines(group.id).map((line) => [lineKey(line), line]))
      const changes: { key: string; before: FinancialAdjustment | null; after: FinancialAdjustment | null }[] = []
      const latest = new Map<string, FinancialAdjustment | null>()
      for (const edit of edits) {
        const line = lines.get(edit.key)
        if (!line || !stateOf(line).open) continue
        const before = latest.has(edit.key) ? latest.get(edit.key)! : adjustmentOf(line)
        const next: FinancialAdjustment = {
          customerPrice: before?.customerPrice ?? null,
          exclude: before?.exclude ?? false,
          note: before?.note ?? '',
          userId: owner.value,
          userName: userName.value,
          changedAt: new Date().toISOString(),
        }
        if (edit.field === 'price') {
          const text = String(edit.value).trim()
          const price = text ? financialDecimal(text) : null
          if (text && price === null) continue
          next.customerPrice = price === null ? null : roundFinancialAmount(price, 2)
        } else if (edit.field === 'bill') next.exclude = !edit.value
        else next.note = String(edit.value)
        const after = next.customerPrice === null && !next.exclude && !next.note.trim() ? null : next
        if (sameAdjustment(before, after)) continue
        latest.set(edit.key, after)
        const first = changes.find((change) => change.key === edit.key)
        if (first) first.after = after
        else changes.push({ key: edit.key, before, after })
      }
      if (!changes.length) return
      applyAdjustments(changes.map((change) => [change.key, change.after]))
      undoStack.value = [...undoStack.value.slice(-49), changes]
      redoStack.value = []
    }
    const undoGrid = (): void => {
      const changes = undoStack.value[undoStack.value.length - 1]
      if (!changes) return
      undoStack.value = undoStack.value.slice(0, -1)
      redoStack.value = [...redoStack.value, changes]
      applyAdjustments(changes.map((change) => [change.key, change.before]))
    }
    const redoGrid = (): void => {
      const changes = redoStack.value[redoStack.value.length - 1]
      if (!changes) return
      redoStack.value = redoStack.value.slice(0, -1)
      undoStack.value = [...undoStack.value, changes]
      applyAdjustments(changes.map((change) => [change.key, change.after]))
    }
    // Saves the pending grid changes per invoice file. Later edits to the same row wait for the next save.
    const flushAdjustments = async (): Promise<void> => {
      const snapshot = new Map(pendingAdjustments.value)
      if (!snapshot.size) return
      // Row ids can contain ':', so keys are matched against each invoice file's id prefix.
      const byBatch = new Set(imports.value.filter((batch) => [...snapshot.keys()].some((key) => key.startsWith(`${batch.id}:`))).map((batch) => batch.id))
      gridSave.value = 'saving'
      try {
        await persist(
          imports.value
            .filter((batch) => byBatch.has(batch.id))
            .map((batch) => ({
              ...batch,
              charges: batch.charges.map((row) => {
                const key = `${batch.id}:${row.id}`
                if (!snapshot.has(key) || financialReady(batch, row) || row.review?.status === 'excluded') return row
                const next: FinancialCharge = { ...row }
                const adjustment = snapshot.get(key)
                if (adjustment) next.adjustment = adjustment
                else delete next.adjustment
                return next
              }),
            })),
        )
        const remaining = new Map(pendingAdjustments.value)
        for (const [key, value] of snapshot) if (remaining.get(key) === value) remaining.delete(key)
        pendingAdjustments.value = remaining
        gridSave.value = remaining.size ? 'saving' : 'saved'
      } catch (reason) {
        gridSave.value = 'error'
        reportError(reason)
      }
    }
    // Waits until every grid change is saved, before anything else writes the same invoice files.
    const settleAdjustments = async (): Promise<void> => {
      window.clearTimeout(gridTimer)
      if (pendingAdjustments.value.size) gridChain = gridChain.then(flushAdjustments)
      await gridChain
    }
    // An action that writes invoice files first saves the review grid; if that fails, the action does not run.
    const gridSettled = async (): Promise<boolean> => {
      await settleAdjustments()
      if (!pendingAdjustments.value.size) return true
      error.value = 'Your changes in the review grid are not saved. Retry saving them in the review before continuing.'
      return false
    }
    // "Retry saving" in the review grid: the failed save's message gives way to the new attempt. When a decision made
    // elsewhere already replaced every pending change, nothing is left to save.
    const retryGrid = (): void => {
      error.value = ''
      if (pendingAdjustments.value.size) applyAdjustments([])
      else gridSave.value = 'saved'
    }
    const linkShipment = async (itemId: string): Promise<void> => {
      if (busy.value || !(await gridSettled())) return
      const entry = ledger.value.find((item) => item.shipment.itemId === itemId)
      if (!entry) return
      const rows = entry.lines.filter((line) => line.row.matchStatus === 'unmatched')
      const rowsByBatch = new Map<string, Set<string>>()
      for (const line of rows) rowsByBatch.set(line.batch.id, new Set([...(rowsByBatch.get(line.batch.id) ?? []), line.row.id]))
      const match = financialMatchFromRecord(entry.shipment)
      busy.value = true
      error.value = ''
      try {
        await persist(
          imports.value
            .filter((batch) => rowsByBatch.has(batch.id))
            .map((batch) => ({
              ...batch,
              // A grid decision made while the row was unlinked does not carry over to the shipment it is linked to.
              charges: batch.charges.map((row) =>
                rowsByBatch.get(batch.id)!.has(row.id) ? { ...dropAdjustment(batch.id, row), match, matchStatus: 'matched' as const } : row,
              ),
            })),
        )
        message.value = `${rows.length} invoice ${rows.length === 1 ? 'row was' : 'rows were'} linked to this shipment.`
        // The Link button is gone once its rows are linked, so the shipment's title takes the focus.
        await nextTick()
        document.getElementById('financial-shipment-title')?.focus()
      } catch (reason) {
        reportError(reason)
      } finally {
        busy.value = false
      }
    }
    const downloadOriginal = async (): Promise<void> => {
      if (!active.value) return
      const batch = active.value
      const version = session
      try {
        const original = await financialOriginal(owner.value, batch.id)
        if (version === session && canAccess.value) downloadFinancialFile(original, batch.fileName)
      } catch (reason) {
        if (version === session) reportError(reason)
      }
    }
    // Recording happens first: if another tab changed the draft, nothing is downloaded.
    const recordAndDownload = async (
      updates: FinancialImport[],
      lines: FinancialLine[],
      kind: FinancialExportKind,
      scope: Parameters<typeof financialExportWorkbook>[2],
      fileName: string,
    ): Promise<void> => {
      const version = session
      const plan = kind === 'review' ? null : financialBillingPlan(lines, scope?.quotedShipments ?? [], quotes.value, imports.value, {
        createdAt: new Date().toISOString(), userName: userName.value, fileName,
      })
      const workbook = financialExportWorkbook(lines, kind, { ...scope, billingAmounts: plan?.amounts })
      if (plan) {
        for (const batch of updates) {
          const latest = batch.exports[batch.exports.length - 1]
          latest.period = scope?.billingPeriod
          for (const row of batch.charges) {
            const amount = plan.amounts[financialBillingKey({ batch, row })]
            if (amount && latest.snapshot?.[row.id]) {
              latest.snapshot[row.id].billedAmount = amount.amount
              latest.snapshot[row.id].currency = batch.currency
              latest.snapshot[row.id].itemId = row.match?.itemId
            }
          }
        }
        await saveFinancialBilling(owner.value, { imports: imports.value, quotes: quotes.value, shipments: shipments.value }, updates, plan.quotes)
        if (version !== session || !canAccess.value) return
        const changed = new Map(updates.map((batch) => [batch.id, batch]))
        imports.value = imports.value.map((batch) => changed.get(batch.id) ?? batch)
        if (active.value && changed.has(active.value.id)) active.value = changed.get(active.value.id)!
        quotes.value = plan.quotes
      } else await persist(updates)
      if (version !== session || !canAccess.value) return
      downloadFinancialFile(
        new Blob([new Uint8Array(workbook).buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        fileName,
      )
    }
    // The snapshot keeps what each row was billed at, so a later price or customer change shows as changed after billing.
    const record = (kind: FinancialExportKind, rows: FinancialCharge[], fileName: string, scopeId: string | null, scopeName: string): FinancialExportRecord => ({
      createdAt: new Date().toISOString(),
      userName: userName.value,
      count: rows.length,
      kind,
      customerId: scopeId,
      customerName: scopeName,
      rowIds: rows.map((row) => row.id),
      snapshot:
        kind === 'review'
          ? undefined
          : Object.fromEntries(
              rows.map((row) => [row.id, { customerPrice: row.review?.customerPrice ?? null, billingId: financialBillingId(row.match) }]),
            ),
      fileName,
    })
    const lastBilled = (rows: FinancialCharge[]): string => {
      const batch = active.value!
      const ids = new Set(rows.map((row) => row.id))
      const last = [...batch.exports].reverse().find((item) => item.kind !== 'review' && item.rowIds?.some((id) => ids.has(id)))
      return last ? `Last billed on ${dateLabel(last.createdAt)} by ${last.userName} (${last.customerName || 'All customers'})` : 'Already billed'
    }
    // The invoice header's downloads: the billing basis of this invoice (all customers, or the customer chosen under
    // "All charges") and the review report. Customer specifications are made on the customer's page.
    const exportFile = async (kind: 'billing' | 'review', includeBilled?: boolean): Promise<void> => {
      if (!active.value || busy.value || matching.value || !(await gridSettled())) return
      const batch = active.value
      error.value = ''
      message.value = ''
      const billed = exportReady.value.filter((row) => signalsOf(row).billed)
      if (kind === 'billing' && billed.length && includeBilled === undefined) {
        // One question under the header at a time.
        deleteCandidate.value = ''
        exportPrompt.value = { fresh: exportFresh.value.length, billed: billed.length, last: lastBilled(billed) }
        await nextTick()
        document.getElementById(exportFresh.value.length ? 'financial-reexport-new' : 'financial-reexport-include')?.focus()
        return
      }
      exportPrompt.value = null
      const rows = kind === 'review' ? exportRows.value : includeBilled ? exportReady.value : exportFresh.value
      const included = new Set(rows.map((row) => row.id))
      const outside = exportRows.value.filter((row) => kind === 'review' ? !included.has(row.id) : !financialReady(batch, row))
      const scopeId = customerId.value === 'all' ? null : customerId.value
      const fileName = financialExportFileName(kind, [batch.carrier, batch.invoiceNumber || 'draft', scopeId ? exportCustomerLabel.value : 'all-customers'])
      busy.value = true
      try {
        const amounts = outside.map((row) => financialChargeAmount(batch, row))
        await recordAndDownload(
          [{ ...batch, exports: [...batch.exports, record(kind, rows, fileName, scopeId, exportCustomerLabel.value)] }],
          rows.map((row) => ({ batch, row })),
          kind,
          {
            reviewSelection: 'all',
            customerId: scopeId,
            customerName: exportCustomerLabel.value,
            uncoveredAmount: amounts.some((value) => value === null) ? null : sumFinancialAmounts(amounts as string[]),
            uncoveredRows: outside.length,
            previouslyBilled: rows.filter((row) => signalsOf(row).billed || signalsOf(row).changed).length,
            outside: outside.map((row) => ({ batch, row })),
          },
          fileName,
        )
        message.value =
          kind === 'review'
            ? `Review report with ${rows.length} rows downloaded, including matched and unmatched rows. Review and billing status are unchanged.`
            : `${exportKindLabel(kind)} for ${exportCustomerLabel.value} downloaded with ${rows.length} rows. Downloads are recorded here; Jeeves is not updated.`
      } catch (reason) {
        reportError(reason)
      } finally {
        busy.value = false
      }
    }
    // The Bill section on a customer's page. Its result is shown next to its buttons, not under the decision bar.
    const exportCustomer = async (kind: 'billing' | 'specification', selection: FinancialBillingSelection, lines: FinancialLine[]): Promise<void> => {
      if (busy.value) return
      error.value = ''
      message.value = ''
      billingNotice.value = ''
      billingFailure.value = ''
      if (!(await gridSettled())) {
        // The grid's own status offers "Retry saving".
        billingFailure.value = error.value
        error.value = ''
        return
      }
      const quotedShipments = financialQuoteCandidates(ledger.value, quotes.value, selection)
      if (!lines.length && !quotedShipments.length) return
      const name = lines.length ? financialBillingName(lines[0].row.match) : quotedShipments[0].billingName
      const period = selection.from || selection.to ? `${selection.from || 'start'} – ${selection.to || 'latest'}` : ''
      // Open and excluded rows of the customer and period are listed as not included, with their cost.
      const groups = financialCustomerLines(imports.value, selection)
      const open = [...groups.open, ...groups.excluded]
      const openAmounts = open.map((line) => financialChargeAmount(line.batch, line.row))
      const fileName = financialExportFileName(kind, [name, period.replace(' – ', '_to_')])
      const byBatch = new Map<string, FinancialLine[]>()
      for (const line of lines) byBatch.set(line.batch.id, [...(byBatch.get(line.batch.id) ?? []), line])
      busy.value = true
      try {
        await recordAndDownload(
          [...byBatch.values()].map((batchLines) => ({
            ...batchLines[0].batch,
            exports: [
              ...batchLines[0].batch.exports,
              record(kind, batchLines.map((line) => line.row), fileName, selection.customerId, name),
            ],
          })),
          lines,
          kind,
          {
            customerId: selection.customerId,
            customerName: name,
            period,
            billingPeriod: { from: selection.from, to: selection.to, basis: 'imported' },
            quotedShipments,
            uncoveredAmount: openAmounts.some((value) => value === null) ? null : sumFinancialAmounts(openAmounts as string[]),
            uncoveredRows: open.length,
            outside: open,
            previouslyBilled: lines.filter((line) => financialBillingState(line.batch, line.row.id) !== 'none').length,
          },
          fileName,
        )
        billingNotice.value = `${exportKindLabel(kind)} for ${name} downloaded with ${lines.length} cost rows and ${quotedShipments.length} quoted bookings from ${byBatch.size} ${byBatch.size === 1 ? 'invoice' : 'invoices'}. Downloads are recorded here; Jeeves is not updated.`
      } catch (reason) {
        billingFailure.value =
          reason instanceof Error ? reason.message : 'The export could not be completed. Retry; nothing was recorded or downloaded.'
      } finally {
        busy.value = false
      }
    }
    const exportReview = (): Promise<void> => exportFile('review')
    const exportCombinedReview = async (): Promise<void> => {
      if (busy.value || matching.value || !reportLines.value.length || !(await gridSettled())) return
      const batches = reportImports.value
      const lines = reportLines.value
      const fileName = financialExportFileName('review', ['combined', `${batches.length}-invoices`])
      error.value = ''
      message.value = ''
      busy.value = true
      try {
        await recordAndDownload(
          batches.map((batch) => ({ ...batch, exports: [...batch.exports, record('review', batch.charges, fileName, null, 'All customers')] })),
          lines, 'review', { reviewSelection: 'all', customerName: 'All customers', uncoveredRows: 0, uncoveredAmount: '0' }, fileName,
        )
        message.value = `Combined report downloaded: ${batches.length} invoices, ${lines.length} rows, including matched and unmatched rows. Review and billing status are unchanged.`
      } catch (reason) {
        reportError(reason)
      } finally {
        busy.value = false
      }
    }
    const exportBilling = (): Promise<void> => exportFile('billing')
    // Closing the prompt, by exporting or cancelling, hands focus back to the export button that opened it.
    const focusExport = async (): Promise<void> => {
      await nextTick()
      document.getElementById('financial-export-billing')?.focus()
    }
    const confirmExport = async (includeBilled: boolean): Promise<void> => {
      if (!exportPrompt.value) return
      await exportFile('billing', includeBilled)
      if (!exportPrompt.value) await focusExport()
    }
    const cancelExport = (): Promise<void> => {
      exportPrompt.value = null
      return focusExport()
    }
    // "Delete import…" in the invoice header asks first, in place.
    const askDelete = async (id: string): Promise<void> => {
      exportPrompt.value = null
      deleteCandidate.value = id
      await nextTick()
      document.getElementById('financial-delete-confirm')?.focus()
    }
    const cancelDelete = async (): Promise<void> => {
      deleteCandidate.value = ''
      await nextTick()
      document.getElementById('financial-delete-import')?.focus()
    }
    const deleteImport = async (id: string): Promise<void> => {
      if (busy.value) return
      const batch = imports.value.find((item) => item.id === id)
      busy.value = true
      error.value = ''
      try {
        await deleteFinancialImport(owner.value, id)
        imports.value = imports.value.filter((item) => item.id !== id)
        deleteCandidate.value = ''
        // The deleted invoice was open: back to the imports, where the message says what happened.
        if (active.value?.id === id) {
          active.value = null
          resetFilters()
        }
        message.value = `The import of ${batch?.fileName ?? 'the invoice'} was deleted from this browser.`
        await nextTick()
        document.getElementById('financial-imports-title')?.focus()
      } catch (reason) {
        reportError(reason)
      } finally {
        busy.value = false
      }
    }
    // Unsaved review inputs are kept per row while navigating; warn before the tab closes.
    const beforeUnload = (event: BeforeUnloadEvent): void => {
      if (!Object.keys(drafts.value).length && !pendingAdjustments.value.size) return
      event.preventDefault()
      event.returnValue = ''
    }
    onMounted(() => window.addEventListener('beforeunload', beforeUnload))
    // Leaving Financial saves the review grid first and asks before unsaved changes are lost. Only a routed page
    // (inside a router view) can guard navigation.
    if (inject(matchedRouteKey, null))
      onBeforeRouteLeave(async () => {
        await settleAdjustments()
        if (!pendingAdjustments.value.size && !Object.keys(drafts.value).length) return true
        return window.confirm('Some changes are not saved and will be lost. Leave Financial anyway?')
      })
    onBeforeUnmount(() => {
      session += 1
      matchController?.abort()
      ledgerController?.abort()
      marginController?.abort()
      window.removeEventListener('beforeunload', beforeUnload)
    })
    return {
      tab,
      tabs,
      ledger,
      quotes,
      withoutShipment,
      selectedShipment,
      financialQuoteEligible,
      selectedQuoteBilling,
      inDetail,
      ledgerLoading,
      ledgerProgress,
      ledgerResult,
      loadedPeriods,
      marginNotice,
      marginLoading,
      retryMargins,
      entryReasons,
      lineSuggestion,
      stateOf,
      reviewCustomer,
      owner,
      overviewCustomers,
      bookingPeriod,
      changeBookingPeriod,
      customerEntries,
      olderEntries,
      showOlder,
      toggleOlder,
      customerScopeLabel,
      customerEmptyText,
      selectedGroup,
      groupRow,
      groupData,
      openGroup,
      closeGroup,
      approveSelectedGroup,
      billCustomer,
      goShipments,
      goInvoices,
      gridRows,
      gridSave,
      undoStack,
      redoStack,
      editGrid,
      undoGrid,
      redoGrid,
      retryGrid,
      approveGroup,
      selectedSuggestion,
      selectedMargin,
      selectedUnpriced,
      billingPeriod,
      billingNotice,
      billingFailure,
      rememberBilling,
      returnTarget,
      detailBlocker,
      cancelLoad,
      showTab,
      onTabKey,
      openShipment,
      closeShipment,
      openLineFromShipment,
      openOrphan,
      eligibleCount,
      loadShipments,
      linkShipment,
      canAccess,
      imports,
      active,
      mappings,
      busy,
      loading,
      matching,
      dragging,
      progress,
      message,
      error,
      metadataError,
      fileInput,
      invoiceNumber,
      currency,
      amountBasis,
      invoiceTotal,
      view,
      filter,
      customerId,
      search,
      page,
      selectedId,
      selectedRow,
      selectedSignals,
      invoiceDetailsVisible,
      selection,
      selectionSet,
      selectedRows,
      allOnPageSelected,
      drafts,
      deleteCandidate,
      exportPrompt,
      nextReviewId,
      openInvoiceDetails,
      closeInvoiceDetails,
      customerSummaries,
      coverage,
      matchedCount,
      metadataComplete,
      invoiceLabel,
      invoiceMeta,
      uploadHint,
      reconciliation,
      totalsText,
      matchedText,
      reopenCount,
      selectAllCount,
      filteredRows,
      pageRows,
      pageCount,
      exportReady,
      exportMatched,
      exportCustomerLabel,
      reviewExportHint,
      exportRows,
      customerScope,
      showBookedCustomers,
      showInvoiceCustomers,
      excludedReportInvoices,
      reportImports,
      reportLines,
      reportMatchedCount,
      toggleReportInvoice,
      toggleAllReportInvoices,
      lastDownload,
      currencies: FINANCIAL_CURRENCIES,
      amountLabel: financialAmountLabel,
      chargeAmount: financialChargeAmount,
      ready: financialReady,
      signalsOf,
      billingName: financialBillingName,
      draftKey,
      dateLabel,
      numberLabel,
      isNegative,
      marginLabel,
      importStatus,
      openFromList,
      backToImports,
      onFileChange,
      onDrop,
      onDragOver,
      onDragLeave,
      matchImport,
      cancelMatching,
      saveMetadata,
      showCustomers,
      showCharges,
      openCustomer,
      previousPage,
      nextPage,
      toggleRow,
      togglePage,
      selectAllFiltered,
      clearSelection,
      selectCharge,
      closeReview,
      statusLabel,
      rowHint,
      weightNote,
      saveReview,
      bulkExclude,
      saveDraft,
      confirmMapping,
      openEntry,
      downloadOriginal,
      exportReview,
      exportCombinedReview,
      exportBilling,
      exportCustomer,
      confirmExport,
      cancelExport,
      askDelete,
      cancelDelete,
      deleteImport,
    }
  },
})
</script>
